import { useState, useEffect } from 'react';
import axios from 'axios';
import { financeApi } from '../lib/api';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { getInvoiceAmounts, getInvoiceNet } from '../lib/invoiceMath';
import { useTranslation } from '../contexts/TranslationContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { LinkBookedInvoiceDialog } from './LinkBookedInvoiceDialog';
import { UnlinkFromEconomicDialog } from './UnlinkFromEconomicDialog';
import { QuoteSection } from './QuoteSection';
import { deriveRfqStatus, RFQ_STATE, isUnitPriceUnknown } from '../lib/rfq';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { API_BASE } from '../lib/apiBase';
import { 
    DollarSign,
    Receipt,
    FileText,
    Truck,
    Package,
    CheckCircle,
    Check,
    Clock,
    Lock,
    AlertTriangle,
    AlertCircle,
    Edit2,
    Pencil,
    BookOpen,
    ArrowRight,
    TrendingUp,
    History,
    Palette,
    ExternalLink,
    Plus,
    Trash2,
    ShoppingCart,
    Search,
    ChevronUp,
    ChevronDown,
    Wand2,
    X,
    Download,
    Send,
    RefreshCw,
    Loader2,
    Link as LinkIcon,
    Unlink as UnlinkIcon,
    Stethoscope
} from 'lucide-react';

// Helper function to get the production line item name based on product type
const getProductionLineItemName = (order) => {
    if (!order) return 'Production';
    
    const productType = order.product_type || 'vinyl';
    const title = order.title || '';
    
    switch (productType) {
        case 'vinyl':
            return `Vinyl Production - ${order.format || 'LP'}`;
        case 'cd':
            return `CD Replication${title ? `: ${title}` : ''}`;
        case 'dvd':
            return `DVD Replication${title ? `: ${title}` : ''}`;
        case 'bluray':
            return `Blu-ray Replication${title ? `: ${title}` : ''}`;
        default:
            return `${productType} Production${title ? `: ${title}` : ''}`;
    }
};

// Status badge for purchase/invoice
const FinanceStatusBadge = ({ status, type }) => {
    const styles = {
        draft: 'bg-amber-100 text-amber-700',
        draft_pending: 'bg-amber-100 text-amber-700',
        ready_to_book: 'bg-blue-100 text-blue-700',
        confirmed: 'bg-blue-100 text-blue-700',
        dispatched: 'bg-purple-100 text-purple-700',
        booked: 'bg-emerald-100 text-emerald-700',
        not_invoiced: 'bg-slate-100 text-slate-500',
        sent: 'bg-emerald-100 text-emerald-700',
        ordered: 'bg-blue-100 text-blue-700',
        invoiced: 'bg-emerald-100 text-emerald-700',
        paid: 'bg-emerald-100 text-emerald-700',
    };
    
    const labels = {
        draft: 'Draft',
        draft_pending: 'Draft – Pending Dispatch',
        ready_to_book: 'Ready to Book',
        confirmed: 'Confirmed',
        dispatched: 'Dispatched',
        booked: 'Booked',
        not_invoiced: 'Not Invoiced',
        sent: 'Sent',
        ordered: 'Ordered',
        invoiced: 'Invoiced',
        paid: 'Paid',
    };
    
    const normalizedStatus = status?.toLowerCase();
    
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${styles[normalizedStatus] || 'bg-slate-100 text-slate-600'}`}>
            {normalizedStatus === 'booked' && <Lock className="h-3 w-3" />}
            {(normalizedStatus === 'draft' || normalizedStatus === 'draft_pending') && <Clock className="h-3 w-3" />}
            {normalizedStatus === 'ready_to_book' && <CheckCircle className="h-3 w-3" />}
            {normalizedStatus === 'invoiced' && <Lock className="h-3 w-3" />}
            {normalizedStatus === 'paid' && <CheckCircle className="h-3 w-3" />}
            {labels[normalizedStatus] || status}
        </span>
    );
};

// ============================================================
// UNIFIED SALES DOCUMENT CARD COMPONENT
// Shows the same commercial document at different lifecycle stages:
// Proforma/Estimate → Ready to Invoice → Invoice Draft → Booked
// ============================================================
const SalesDocumentCard = ({
    invoice,
    proforma,
    financials,
    isEditingInvoice,
    setIsEditingInvoice,
    isEditingProforma,
    setIsEditingProforma,
    invoiceForm,
    setInvoiceForm,
    proformaForm,
    setProformaForm,
    handleSaveInvoice,
    handleSaveProforma,
    handleBookInvoice,
    handleCreateInvoiceDraft,
    handleSyncFromEconomic,
    isSaving,
    isBooking,
    isCreatingInvoice,
    isSyncingFromEconomic,
    isInvoiceBooked,
    canBookInvoice,
    isNoSupplierMode,
    formatDateTime,
    orderId,
    refetchInvoice,
    t
}) => {
    // Manual recovery dialog state — opened when admin needs to link a
    // booked e-conomic invoice that auto-sync couldn't resolve.
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    // Administrative unlink — sever the OwnTune ↔ e-conomic pointer
    // without touching e-conomic. Shown once the invoice IS linked.
    const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
    const [diagnosing, setDiagnosing] = useState(false);

    // Already-booked invoices must NOT be modifiable from OwnTune — the
    // backend now 409s on `book`, mirror that intent in the UI by
    // disabling/hiding the "Update in e-conomic" button below.
    const isAlreadyBookedInEconomic = !!(
        invoice?.economic_status === 'booked_in_economic'
        || invoice?.economic_booked_invoice_number
    );

    const handleDiagnose = async () => {
        setDiagnosing(true);
        try {
            const res = await financeApi.syncDiagnostics(orderId);
            // Render a structured snapshot in the toast so admin can copy/paste
            // it into a support ticket. We also log the full report to the
            // browser console for deeper inspection.
            const report = res.data || {};
             
            console.info('[e-conomic-sync-diagnostics]', report);
            const rec = report.recommendation || {};
            const summary = [
                `Stored draft: #${report.stored_draft_number ?? '—'}`,
                `Stored booked: #${report.stored_booked_number ?? '—'}`,
                `Customer: ${report.customer_number ?? '—'}`,
                `Recommendation: ${rec.action || '—'}${rec.reason ? ` (${rec.reason})` : ''}`,
                rec.booked_invoice_number ? `Auto-link target: #${rec.booked_invoice_number}` : null,
            ].filter(Boolean).join('\n');
            toast.message('Sync diagnostics', {
                description: summary,
                duration: 12000,
            });
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to run diagnostics');
        } finally {
            setDiagnosing(false);
        }
    };

    // Determine lifecycle stage
    const getLifecycleStage = () => {
        if (isInvoiceBooked) return 'booked';
        if (invoice?.status === 'ready_to_book') return 'ready_to_book';
        if (invoice) return 'invoice_draft';
        if (proforma?.has_proforma) return 'proforma';
        return 'no_pricing';
    };
    
    const stage = getLifecycleStage();
    
    // Stage styling
    const stageStyles = {
        no_pricing: { bg: 'bg-slate-50', border: 'border-dashed border-slate-200', icon: 'text-slate-400' },
        proforma: { bg: 'bg-amber-50/50', border: 'border-amber-200', icon: 'text-amber-600' },
        invoice_draft: { bg: 'bg-blue-50/50', border: 'border-blue-200', icon: 'text-blue-600' },
        ready_to_book: { bg: 'bg-emerald-50/50', border: 'border-emerald-200', icon: 'text-emerald-600' },
        booked: { bg: 'bg-emerald-50', border: 'border-emerald-300', icon: 'text-emerald-600' },
        booked_in_economic: { bg: 'bg-emerald-100', border: 'border-emerald-400', icon: 'text-emerald-700' }
    };
    
    // Get the actual e-conomic status for better labeling
    const economicStatus = invoice?.economic_status || (invoice?.economic_booked ? 'draft_in_economic' : 'local_draft');
    
    // Determine display badge based on e-conomic status
    const getBadgeInfo = () => {
        if (!invoice) {
            if (proforma?.has_proforma) return { label: 'Proforma', color: 'bg-amber-100 text-amber-700' };
            return { label: 'No Pricing', color: 'bg-slate-100 text-slate-600' };
        }
        
        // Invoice exists - check e-conomic status
        if (economicStatus === 'booked_in_economic' || invoice.economic_booked_invoice_number) {
            return { label: 'Booked in e-conomic', color: 'bg-emerald-200 text-emerald-800' };
        }
        if (economicStatus === 'draft_in_economic' || invoice.economic_booked) {
            return { label: 'Draft in e-conomic', color: 'bg-blue-200 text-blue-800' };
        }
        // Distinguish "was linked, now unlinked" from "never sent"
        // so admins immediately see recovery is available (via Sync
        // or the "Link existing e-conomic invoice" button).
        if (economicStatus === 'unlinked' || invoice.economic_last_unlinked_at) {
            return { label: 'Unlinked from e-conomic', color: 'bg-amber-100 text-amber-800' };
        }
        if (invoice.status === 'ready_to_book') {
            return { label: 'Ready to Book', color: 'bg-emerald-100 text-emerald-700' };
        }
        return { label: 'Local Draft', color: 'bg-blue-100 text-blue-700' };
    };
    
    const stageBadges = {
        no_pricing: { label: 'No Pricing', color: 'bg-slate-100 text-slate-600' },
        proforma: { label: 'Proforma', color: 'bg-amber-100 text-amber-700' },
        invoice_draft: { label: 'Invoice Draft', color: 'bg-blue-100 text-blue-700' },
        ready_to_book: { label: 'Ready to Book', color: 'bg-emerald-100 text-emerald-700' },
        booked: { label: 'Booked', color: 'bg-emerald-100 text-emerald-700' }
    };
    
    const style = stageStyles[stage];
    const badge = getBadgeInfo(); // Use dynamic badge based on e-conomic status
    
    // Get display values - prefer invoice data over proforma
    // Invoice totals use the shared invoice-math helper so that VAT-inclusive
    // invoices (where the stored `total_amount` is gross) still render NET as
    // the subtotal. See `frontend/src/lib/invoiceMath.js`.
    const quantity = invoice?.quantity || proforma?.quantity || 0;
    const unitPrice = invoice?.unit_price || proforma?.unit_price || null;
    const currency = invoice?.currency || proforma?.currency || 'EUR';
    const invoiceAmounts = getInvoiceAmounts(invoice);
    const invoiceNet = invoiceAmounts.net;
    const invoiceGross = invoiceAmounts.gross;
    const invoiceVat = invoiceAmounts.vat;
    const total = invoiceNet ?? proforma?.total_revenue ?? null;
    const invoiceNumber = invoice?.invoice_number || null;
    
    // EUR normalization
    const normalizedEur = financials?.actualRevenue || financials?.estimatedRevenue || null;
    const showNormalized = currency !== 'EUR' && normalizedEur;
    
    // Editing mode - use invoice form if invoice exists, otherwise proforma form
    const isEditing = invoice ? isEditingInvoice : isEditingProforma;
    const setIsEditing = invoice ? setIsEditingInvoice : setIsEditingProforma;
    const handleSave = invoice ? handleSaveInvoice : handleSaveProforma;
    
    // Can edit?
    const canEdit = !isInvoiceBooked && stage !== 'no_pricing';
    
    return (
        <div className={`p-4 rounded-lg border ${style.bg} ${style.border}`} data-testid="sales-document-card">
            {/* Header with lifecycle indicator */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Receipt className={`h-4 w-4 ${style.icon}`} />
                    <h4 className="text-sm font-semibold text-slate-700">Sales Document</h4>
                    <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${badge.color}`}>
                        {badge.label}
                    </span>
                    {isInvoiceBooked && <Lock className="h-3 w-3 text-emerald-600" />}
                </div>
                {canEdit && !isEditing && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setIsEditing(true)}
                    >
                        <Edit2 className="h-3 w-3 mr-1" />
                        Edit
                    </Button>
                )}
            </div>
            
            {/* Lifecycle Progress Bar */}
            <div className="flex items-center gap-1 mb-4 pb-3 border-b border-slate-200">
                {['proforma', 'invoice_draft', 'ready_to_book', 'booked'].map((s, idx) => {
                    const isComplete = ['proforma', 'invoice_draft', 'ready_to_book', 'booked'].indexOf(stage) >= idx;
                    const isCurrent = stage === s;
                    return (
                        <div key={s} className="flex items-center">
                            <div className={`h-1.5 w-12 rounded-full ${isComplete ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                            {idx < 3 && <div className="w-1" />}
                        </div>
                    );
                })}
                <span className="ml-2 text-[10px] text-slate-500">
                    {stage === 'proforma' && 'Estimate'}
                    {stage === 'invoice_draft' && 'Draft Invoice'}
                    {stage === 'ready_to_book' && 'Ready'}
                    {stage === 'booked' && 'Complete'}
                </span>
            </div>
            
            {/* No Pricing State */}
            {stage === 'no_pricing' && !isNoSupplierMode && (
                <div className="text-center py-4">
                    <p className="text-sm text-slate-500 mb-3">Set estimated pricing for early profit visibility</p>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsEditingProforma(true)}
                    >
                        <Plus className="h-3 w-3 mr-1" />
                        Set Proforma Price
                    </Button>
                </div>
            )}
            
            {/* No Supplier Mode - Direct Invoice Creation */}
            {stage === 'no_pricing' && isNoSupplierMode && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                        <Truck className="h-4 w-4" />
                        <span>No supplier workflow — create invoice directly</span>
                    </div>
                    <Button
                        size="sm"
                        className="h-8 text-xs bg-blue-600 hover:bg-blue-700"
                        onClick={handleCreateInvoiceDraft}
                        disabled={isCreatingInvoice}
                        data-testid="create-invoice-draft-btn"
                    >
                        <Receipt className="h-3 w-3 mr-1" />
                        {isCreatingInvoice ? 'Creating...' : 'Create Draft Invoice'}
                    </Button>
                </div>
            )}
            
            {/* Edit Mode */}
            {isEditing && stage !== 'no_pricing' && (
                <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                        <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Quantity</Label>
                            {invoice ? (
                                <Input
                                    type="number"
                                    value={invoiceForm.quantity}
                                    onChange={(e) => setInvoiceForm({ ...invoiceForm, quantity: e.target.value })}
                                    className="h-8 text-sm"
                                />
                            ) : (
                                <p className="text-sm font-medium text-slate-700 py-1">{proforma?.quantity || 0} pcs</p>
                            )}
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Unit Price</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={invoice ? invoiceForm.unit_price : proformaForm.unit_price}
                                onChange={(e) => invoice 
                                    ? setInvoiceForm({ ...invoiceForm, unit_price: e.target.value })
                                    : setProformaForm({ unit_price: e.target.value })
                                }
                                className="h-8 text-sm"
                                placeholder="0.00"
                            />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Currency</Label>
                            {invoice ? (
                                <Select
                                    value={invoiceForm.currency}
                                    onValueChange={(v) => setInvoiceForm({ ...invoiceForm, currency: v })}
                                >
                                    <SelectTrigger className="h-8 text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="EUR">EUR</SelectItem>
                                        <SelectItem value="DKK">DKK</SelectItem>
                                        <SelectItem value="NOK">NOK</SelectItem>
                                    </SelectContent>
                                </Select>
                            ) : (
                                <p className="text-sm font-medium text-slate-700 py-1">{proforma?.currency || 'EUR'}</p>
                            )}
                        </div>
                        <div className="space-y-1">
                            <Label className="text-xs text-slate-500">Total</Label>
                            <p className="text-sm font-bold text-blue-600 py-1">
                                {(() => {
                                    const qty = invoice ? parseFloat(invoiceForm.quantity) : proforma?.quantity;
                                    const price = invoice ? parseFloat(invoiceForm.unit_price) : parseFloat(proformaForm.unit_price);
                                    const curr = invoice ? invoiceForm.currency : proforma?.currency;
                                    return qty && price ? `${(qty * price).toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${curr}` : '—';
                                })()}
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setIsEditing(false);
                                if (invoice) {
                                    setInvoiceForm({
                                        quantity: invoice.quantity?.toString() || '',
                                        unit_price: invoice.unit_price?.toString() || '',
                                        currency: invoice.currency || 'EUR'
                                    });
                                } else {
                                    setProformaForm({ unit_price: proforma?.unit_price?.toString() || '' });
                                }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSave}
                            disabled={isSaving}
                            className={invoice ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                </div>
            )}
            
            {/* Display Mode */}
            {!isEditing && stage !== 'no_pricing' && (
                <div className="space-y-3">
                    {/* Main Fields */}
                    <div className="grid grid-cols-5 gap-3 text-sm">
                        {invoiceNumber && (
                            <div>
                                <p className="text-[10px] text-slate-500 uppercase">Invoice #</p>
                                <p className="font-medium text-slate-700">{invoiceNumber}</p>
                            </div>
                        )}
                        <div>
                            <p className="text-[10px] text-slate-500 uppercase">Quantity</p>
                            <p className="font-medium text-slate-700">{quantity} pcs</p>
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-500 uppercase">Unit Price</p>
                            <p className="font-medium text-slate-700">
                                {unitPrice ? `${unitPrice.toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${currency}` : '—'}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-500 uppercase">Subtotal</p>
                            <p className="font-medium text-slate-700">
                                {total ? `${total.toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${currency}` : '—'}
                            </p>
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-500 uppercase">Normalized EUR</p>
                            <p className={`font-medium ${showNormalized ? 'text-slate-700' : 'text-slate-400'}`}>
                                {showNormalized 
                                    ? `${normalizedEur.toLocaleString('da-DK', { minimumFractionDigits: 2 })} EUR`
                                    : currency === 'EUR' ? '(Same)' : '—'
                                }
                            </p>
                        </div>
                    </div>
                    
                    {/* VAT Section - Always visible */}
                    {invoice && (
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg mt-3">
                            <div className="grid grid-cols-4 gap-3 text-sm">
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase">VAT Mode</p>
                                    <p className="font-medium text-slate-700">
                                        {invoice.vat_mode === 'with_vat' 
                                            ? `With VAT (${invoice.vat_rate || 25}%)`
                                            : 'Without VAT (0%)'
                                        }
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase">Subtotal</p>
                                    <p className="font-medium text-slate-700" data-testid="sales-doc-subtotal-net">
                                        {(invoiceNet ?? 0).toLocaleString('da-DK', { minimumFractionDigits: 2 })} {currency}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase">VAT Amount</p>
                                    <p className="font-medium text-slate-700" data-testid="sales-doc-vat-amount">
                                        {(invoiceVat ?? 0).toLocaleString('da-DK', { minimumFractionDigits: 2 })} {currency}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-slate-500 uppercase">Total incl. VAT</p>
                                    <p className="font-bold text-blue-600" data-testid="sales-doc-total-gross">
                                        {(invoiceGross ?? 0).toLocaleString('da-DK', { minimumFractionDigits: 2 })} {currency}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Comparison with Estimate - EUR normalized for accurate comparison */}
                    {invoice && proforma?.has_proforma && (
                        <div className="text-xs pt-3 border-t border-slate-200 space-y-1">
                            <div className="flex justify-between">
                                <span className="text-slate-500">Original estimate:</span>
                                <span className="font-medium text-slate-700">
                                    {proforma.total_revenue?.toLocaleString('da-DK', { minimumFractionDigits: 2 })} {proforma.currency}
                                    {proforma.currency !== 'EUR' && financials?.estimatedRevenue && (
                                        <span className="text-slate-400 ml-1">
                                            ({financials.estimatedRevenue.toLocaleString('da-DK', { minimumFractionDigits: 2 })} EUR)
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-500">Current invoice:</span>
                                <span className="font-medium text-slate-700">
                                    {(invoiceNet ?? 0).toLocaleString('da-DK', { minimumFractionDigits: 2 })} {invoice.currency}
                                    {invoice.currency !== 'EUR' && normalizedEur && (
                                        <span className="text-slate-400 ml-1">
                                            ({normalizedEur.toLocaleString('da-DK', { minimumFractionDigits: 2 })} EUR normalized)
                                        </span>
                                    )}
                                </span>
                            </div>
                            {/* Calculate EUR-based difference */}
                            {(() => {
                                // Get EUR values for comparison
                                const estimateEur = proforma.currency === 'EUR' 
                                    ? proforma.total_revenue 
                                    : (financials?.estimatedRevenue || 0);
                                // Compare NET-vs-NET. `normalizedEur` already comes from the
                                // backend using get_invoice_net(), so it's net-in-EUR.
                                const invoiceEur = invoice.currency === 'EUR' 
                                    ? (invoiceNet ?? 0)
                                    : (normalizedEur || 0);
                                const differenceEur = invoiceEur - estimateEur;
                                
                                if (estimateEur > 0 && invoiceEur > 0) {
                                    return (
                                        <div className="flex justify-between pt-1 border-t border-slate-100">
                                            <span className="text-slate-500">Difference vs estimate:</span>
                                            <span className={`font-semibold ${differenceEur >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                {differenceEur >= 0 ? '+' : ''}{differenceEur.toLocaleString('da-DK', { minimumFractionDigits: 2 })} EUR
                                            </span>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    )}
                    
                    {/* Accounting Status */}
                    <div className="pt-2 border-t border-slate-200">
                        {/* E-conomic Status Display */}
                        {invoice && (
                            <div className="space-y-2">
                                {/* Status based on economic_status */}
                                {economicStatus === 'booked_in_economic' || invoice.economic_booked_invoice_number ? (
                                    <div className="flex items-center gap-2 p-2 bg-emerald-100 border border-emerald-300 rounded text-xs text-emerald-800">
                                        <CheckCircle className="h-4 w-4" />
                                        <div className="flex-1">
                                            <span className="font-medium">Booked in e-conomic</span>
                                            {invoice.economic_booked_invoice_number && (
                                                <span className="ml-2 text-emerald-600">
                                                    Invoice #{invoice.economic_booked_invoice_number}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ) : economicStatus === 'draft_in_economic' || invoice.economic_booked ? (
                                    <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                                        <Clock className="h-4 w-4" />
                                        <div className="flex-1">
                                            <span className="font-medium">Draft in e-conomic</span>
                                            {(invoice.economic_draft_invoice_number || invoice.economic_draft_number) && (
                                                <span className="ml-2 text-blue-600">
                                                    Draft #{invoice.economic_draft_invoice_number || invoice.economic_draft_number}
                                                </span>
                                            )}
                                            {invoice.economic_booked_at && (
                                                <span className="ml-2 text-slate-500">
                                                    • Sent {formatDateTime(invoice.economic_booked_at)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ) : stage === 'ready_to_book' ? (
                                    <div className="flex items-center gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-700">
                                        <CheckCircle className="h-4 w-4" />
                                        <span>Dispatch complete — ready to book to accounting</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
                                        <Clock className="h-4 w-4" />
                                        <span>Local draft — not yet sent to e-conomic</span>
                                    </div>
                                )}
                                
                                {/* Action buttons */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    {/* Sync from e-conomic button - always available when invoice exists */}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-6 text-xs"
                                        onClick={handleSyncFromEconomic}
                                        disabled={isSyncingFromEconomic}
                                        data-testid="sync-from-economic-btn"
                                    >
                                        <RefreshCw className={`h-3 w-3 mr-1 ${isSyncingFromEconomic ? 'animate-spin' : ''}`} />
                                        {isSyncingFromEconomic ? 'Syncing...' : 'Sync from e-conomic'}
                                    </Button>

                                    {/* Book / Update in e-conomic button — HIDDEN once the invoice
                                       is booked in e-conomic. Booked invoices cannot be mutated
                                       from OwnTune; corrections must be credit notes. */}
                                    {isAlreadyBookedInEconomic ? null : (economicStatus === 'draft_in_economic' || invoice.economic_booked) ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 text-xs"
                                            onClick={handleBookInvoice}
                                            disabled={isBooking}
                                            data-testid="update-in-economic-btn"
                                        >
                                            <RefreshCw className={`h-3 w-3 mr-1 ${isBooking ? 'animate-spin' : ''}`} />
                                            {isBooking ? 'Updating...' : 'Update in e-conomic'}
                                        </Button>
                                    ) : stage === 'ready_to_book' && !invoice.economic_booked ? (
                                        <Button
                                            size="sm"
                                            className="h-6 text-xs bg-emerald-600 hover:bg-emerald-700"
                                            onClick={handleBookInvoice}
                                            disabled={isBooking || !invoice?.unit_price || !invoice?.quantity}
                                        >
                                            <BookOpen className="h-3 w-3 mr-1" />
                                            {isBooking ? 'Booking...' : 'Book to e-conomic'}
                                        </Button>
                                    ) : null}

                                    {/* Manual recovery — link an existing e-conomic booked invoice.
                                       Available in three states:
                                         * ``draft_in_economic``           — draft was booked out-of-band
                                         * ``draft_deleted_or_unknown``    — draft is gone; find the real booked one
                                         * ``unlinked``                    — invoice was previously linked, admin unlinked
                                       Hidden once the invoice already shows a booked-in-economic state. */}
                                    {!isAlreadyBookedInEconomic && (
                                        economicStatus === 'draft_in_economic'
                                        || economicStatus === 'draft_deleted_or_unknown'
                                        || economicStatus === 'unlinked'
                                        || invoice.economic_last_unlinked_at
                                    ) ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 text-xs"
                                            onClick={() => setLinkDialogOpen(true)}
                                            data-testid="link-booked-btn"
                                        >
                                            <LinkIcon className="h-3 w-3 mr-1" />
                                            {economicStatus === 'unlinked' || invoice.economic_last_unlinked_at
                                                ? 'Link existing e-conomic invoice'
                                                : 'Link booked invoice'}
                                        </Button>
                                    ) : null}

                                    {/* Diagnose — read-only deep dive into what auto-sync sees in
                                       e-conomic. Shown whenever an e-conomic-side reference is
                                       stored (draft or booked) so admins can debug stuck states. */}
                                    {(invoice.economic_draft_number || invoice.economic_draft_invoice_number || invoice.economic_booked_invoice_number) ? (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 text-xs text-slate-500"
                                            onClick={handleDiagnose}
                                            disabled={diagnosing}
                                            data-testid="diagnose-sync-btn"
                                            title="Run e-conomic sync diagnostics without mutating any state"
                                        >
                                            <Stethoscope className={`h-3 w-3 mr-1 ${diagnosing ? 'animate-spin' : ''}`} />
                                            {diagnosing ? 'Diagnosing...' : 'Diagnose'}
                                        </Button>
                                    ) : null}

                                    {/* Administrative recovery — unlink from e-conomic. Shown
                                       whenever an e-conomic-side reference is stored (draft or
                                       booked). Never touches e-conomic itself — only clears the
                                       OwnTune-side pointer + records the action in the audit log. */}
                                    {(invoice.economic_draft_number || invoice.economic_draft_invoice_number || invoice.economic_booked_invoice_number || invoice.economic_booked) ? (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => setUnlinkDialogOpen(true)}
                                            data-testid="unlink-from-economic-btn"
                                            title="Sever the OwnTune ↔ e-conomic relationship — nothing in e-conomic is deleted or modified"
                                        >
                                            <UnlinkIcon className="h-3 w-3 mr-1" />
                                            Unlink
                                        </Button>
                                    ) : null}
                                </div>

                                {isAlreadyBookedInEconomic ? (
                                    <p className="text-[10px] text-emerald-700 flex items-center gap-1">
                                        <Lock className="h-3 w-3" />
                                        Booked in e-conomic — changes must be made as a credit note.
                                    </p>
                                ) : null}

                                <LinkBookedInvoiceDialog
                                    open={linkDialogOpen}
                                    onOpenChange={setLinkDialogOpen}
                                    orderId={orderId}
                                    onLinked={refetchInvoice}
                                />

                                <UnlinkFromEconomicDialog
                                    open={unlinkDialogOpen}
                                    onOpenChange={setUnlinkDialogOpen}
                                    orderId={orderId}
                                    invoice={invoice}
                                    onUnlinked={refetchInvoice}
                                />
                                
                                {/* Last sync timestamp */}
                                {invoice.economic_last_synced_at && (
                                    <p className="text-[10px] text-slate-400">
                                        Last synced: {formatDateTime(invoice.economic_last_synced_at)}
                                    </p>
                                )}
                            </div>
                        )}
                        
                        {/* Proforma stage message */}
                        {!invoice && stage === 'proforma' && (
                            <div className="flex items-center gap-2 text-xs text-amber-600">
                                <TrendingUp className="h-3 w-3" />
                                <span>Estimate only — invoice will be created after order processing</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================================
// INVOICE BASIS / ORDER BREAKDOWN COMPONENT
// Shows detailed line items that mirror the Quote structure
// Admin can add, edit, delete, and reorder lines
// ============================================================
const InvoiceBasisSection = ({
    orderId,
    invoiceLines,
    setInvoiceLines,
    onRefresh,
    isLocked,
    isDraftInEconomic = false,
    currency = 'EUR',
    invoiceQuantity = null,
    t = (key, fallback) => fallback
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [editingLineId, setEditingLineId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isAutoPopulating, setIsAutoPopulating] = useState(false);
    const [syncStatus, setSyncStatus] = useState(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isApplyingToSales, setIsApplyingToSales] = useState(false);
    
    // Form state for new/edit line
    const [lineForm, setLineForm] = useState({
        description: '',
        quantity: 1,
        unit_price: 0,
        currency: currency,
        notes: '',
        qty_source: 'manual'
    });
    
    // Calculate totals
    const totalAmount = invoiceLines.reduce((sum, line) => sum + (line.total || 0), 0);
    const lineCount = invoiceLines.length;
    
    // Check sync status when invoice lines change
    useEffect(() => {
        const checkSyncStatus = async () => {
            if (!orderId || lineCount === 0) {
                setSyncStatus(null);
                return;
            }
            try {
                const res = await fetch(`${API_BASE}/api/orders/${orderId}/finance/invoice-lines/sync-status`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setSyncStatus(data);
                }
            } catch (err) {
                console.error('Failed to check sync status:', err);
            }
        };
        checkSyncStatus();
    }, [orderId, lineCount, invoiceLines]);
    
    // Handle manual sync
    const handleSync = async () => {
        setIsSyncing(true);
        try {
            const res = await fetch(`${API_BASE}/api/orders/${orderId}/finance/invoice-lines/sync`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(`Synced ${data.lines_updated} line(s) to ${data.synced_to_quantity} pcs`);
                if (onRefresh) onRefresh();
            } else {
                throw new Error('Sync failed');
            }
        } catch (error) {
            toast.error('Failed to sync invoice lines');
        } finally {
            setIsSyncing(false);
        }
    };
    
    // Handle apply to sales document
    const handleApplyToSalesDocument = async () => {
        if (lineCount === 0) {
            toast.error('No invoice lines to apply');
            return;
        }
        
        // Show confirmation if draft exists in e-conomic
        if (isDraftInEconomic) {
            const confirmed = window.confirm(
                'This invoice already exists as a draft in e-conomic.\n\n' +
                'Applying changes here will update the Sales Document. ' +
                'You will need to click "Update in e-conomic" to push these changes to the draft.\n\n' +
                'Do you want to continue?'
            );
            if (!confirmed) return;
        }
        
        setIsApplyingToSales(true);
        try {
            const res = await fetch(`${API_BASE}/api/orders/${orderId}/finance/invoice-lines/apply-to-sales-document`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            if (res.ok) {
                const data = await res.json();
                toast.success(data.message || 'Applied to Sales Document');
                if (isDraftInEconomic) {
                    toast.info('Remember to update the draft in e-conomic', { duration: 5000 });
                }
                if (onRefresh) onRefresh();
            } else {
                const errorData = await res.json();
                throw new Error(errorData.detail || 'Failed to apply');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to apply Invoice Basis to Sales Document');
        } finally {
            setIsApplyingToSales(false);
        }
    };
    
    // Reset form
    const resetForm = () => {
        setLineForm({
            description: '',
            quantity: 1,
            unit_price: 0,
            currency: currency,
            notes: '',
            qty_source: 'manual'
        });
    };
    
    // Start editing a line
    const startEditing = (line) => {
        setEditingLineId(line.id);
        setLineForm({
            description: line.description,
            quantity: line.quantity,
            unit_price: line.unit_price,
            currency: line.currency,
            notes: line.notes || ''
        });
    };
    
    // Save new line
    const handleAddLine = async () => {
        if (!lineForm.description.trim()) {
            toast.error('Description is required');
            return;
        }
        
        setIsSaving(true);
        try {
            await financeApi.addInvoiceLine(orderId, lineForm);
            toast.success('Line added');
            resetForm();
            setIsAdding(false);
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add line');
        } finally {
            setIsSaving(false);
        }
    };
    
    // Update existing line
    const handleUpdateLine = async () => {
        if (!lineForm.description.trim()) {
            toast.error('Description is required');
            return;
        }
        
        setIsSaving(true);
        try {
            await financeApi.updateInvoiceLine(orderId, editingLineId, lineForm);
            toast.success('Line updated');
            setEditingLineId(null);
            resetForm();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update line');
        } finally {
            setIsSaving(false);
        }
    };
    
    // Delete line
    const handleDeleteLine = async (lineId) => {
        if (!window.confirm('Delete this line item?')) return;
        
        try {
            await financeApi.deleteInvoiceLine(orderId, lineId);
            toast.success('Line deleted');
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete line');
        }
    };
    
    // Move line up/down
    const handleMoveUp = async (index) => {
        if (index === 0) return;
        const newOrder = [...invoiceLines];
        [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
        
        try {
            await financeApi.reorderInvoiceLines(orderId, newOrder.map(l => l.id));
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error('Failed to reorder');
        }
    };
    
    const handleMoveDown = async (index) => {
        if (index === invoiceLines.length - 1) return;
        const newOrder = [...invoiceLines];
        [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
        
        try {
            await financeApi.reorderInvoiceLines(orderId, newOrder.map(l => l.id));
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error('Failed to reorder');
        }
    };
    
    // Auto-populate from order config
    const handleAutoPopulate = async () => {
        if (invoiceLines.length > 0) {
            if (!window.confirm('This will replace all existing lines. Continue?')) return;
            // Delete existing lines first
            for (const line of invoiceLines) {
                await financeApi.deleteInvoiceLine(orderId, line.id);
            }
        }
        
        setIsAutoPopulating(true);
        try {
            const result = await financeApi.autoPopulateInvoiceLines(orderId);
            toast.success(`Created ${result.data.lines_created} lines from order`);
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to auto-populate');
        } finally {
            setIsAutoPopulating(false);
        }
    };
    
    // Sync from Flow - generates invoice lines from product configuration
    const [isSyncingFromFlow, setIsSyncingFromFlow] = useState(false);
    
    const handleSyncFromFlow = async () => {
        if (lineCount > 0) {
            const confirmed = window.confirm(
                'This will replace all existing invoice lines with the product configuration breakdown. Continue?'
            );
            if (!confirmed) return;
        }
        
        setIsSyncingFromFlow(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${API_BASE}/api/orders/${orderId}/finance/invoice-lines/sync-from-flow`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            
            // Clone response before reading to avoid "body stream already read" error
            const responseClone = response.clone();
            
            if (!response.ok) {
                try {
                    const err = await response.json();
                    throw new Error(err.detail || 'Failed to sync from flow');
                } catch (jsonError) {
                    // If JSON parsing fails, try to get text
                    const text = await responseClone.text();
                    throw new Error(text || `HTTP ${response.status}: Failed to sync from flow`);
                }
            }
            
            const result = await response.json();
            toast.success(`Created ${result.lines_created} lines from product configuration (Total: ${result.grand_total?.toLocaleString('da-DK')} ${result.currency})`);
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Sync from flow error:', error);
            toast.error(error.message || 'Failed to sync from product configuration');
        } finally {
            setIsSyncingFromFlow(false);
        }
    };
    
    // Download Invoice PDF
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    
    const handleDownloadPdf = async () => {
        if (lineCount === 0) {
            toast.error('Add line items to generate PDF');
            return;
        }
        
        setIsDownloadingPdf(true);
        try {
            const response = await financeApi.downloadInvoicePdf(orderId);
            
            // Extract filename from Content-Disposition header if available
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'Invoice.pdf';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, '');
                }
            }
            
            // Create blob and download
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success('Invoice PDF downloaded');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to download PDF');
        } finally {
            setIsDownloadingPdf(false);
        }
    };
    
    return (
        <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden" data-testid="invoice-basis-section">
            {/* Sync Warning Banner */}
            {syncStatus && (!syncStatus.is_synced || syncStatus.has_potential_mismatch) && (
                <div className={`flex items-center justify-between px-3 py-2 border-b ${
                    !syncStatus.is_synced 
                        ? 'bg-amber-50 border-amber-200' 
                        : 'bg-yellow-50 border-yellow-200'
                }`}>
                    <div className={`flex items-center gap-2 ${
                        !syncStatus.is_synced ? 'text-amber-700' : 'text-yellow-700'
                    }`}>
                        <AlertTriangle className="h-4 w-4" />
                        <span className="text-xs font-medium">
                            {syncStatus.sync_message || 'Invoice Basis may be out of sync'}
                        </span>
                    </div>
                    {!isLocked && (
                        <Button
                            variant="outline"
                            size="sm"
                            className={`h-6 text-xs px-2 ${
                                !syncStatus.is_synced 
                                    ? 'border-amber-300 text-amber-700 hover:bg-amber-100'
                                    : 'border-yellow-300 text-yellow-700 hover:bg-yellow-100'
                            }`}
                            onClick={handleSync}
                            disabled={isSyncing}
                        >
                            <RefreshCw className={`h-3 w-3 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                            {isSyncing ? 'Syncing...' : 'Sync Now'}
                        </Button>
                    )}
                </div>
            )}
            
            {/* Warning banner when invoice is draft in e-conomic */}
            {isDraftInEconomic && !isLocked && (
                <div className="mx-3 mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>
                        This invoice exists as a draft in e-conomic. Changes made here will need to be pushed to e-conomic.
                    </span>
                </div>
            )}
            
            {/* Header - always visible */}
            <div 
                className="flex items-center justify-between px-3 py-2 bg-slate-50 cursor-pointer hover:bg-slate-100"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-700">Invoice Basis</span>
                    <span className="text-xs text-slate-400">
                        ({lineCount} {lineCount === 1 ? 'line' : 'lines'})
                    </span>
                    {lineCount > 0 && (
                        <span className="text-xs font-medium text-slate-600 ml-2">
                            Total: {totalAmount.toLocaleString('da-DK', { minimumFractionDigits: 2 })} {invoiceLines[0]?.currency || currency}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {lineCount > 0 && !isLocked && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleApplyToSalesDocument();
                            }}
                            disabled={isApplyingToSales}
                            title="Apply Invoice Basis total to Sales Document"
                            data-testid="apply-to-sales-btn"
                        >
                            <ArrowRight className="h-3 w-3 mr-1" />
                            {isApplyingToSales ? 'Applying...' : 'Apply to Sales'}
                        </Button>
                    )}
                    {lineCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs px-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadPdf();
                            }}
                            disabled={isDownloadingPdf}
                            data-testid="download-invoice-pdf-btn"
                        >
                            <Download className="h-3 w-3 mr-1" />
                            {isDownloadingPdf ? 'Downloading...' : 'PDF'}
                        </Button>
                    )}
                    {!isLocked && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs px-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleAutoPopulate();
                            }}
                            disabled={isAutoPopulating}
                        >
                            <Wand2 className="h-3 w-3 mr-1" />
                            {isAutoPopulating ? 'Generating...' : 'Auto-fill'}
                        </Button>
                    )}
                    {!isLocked && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-xs px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSyncFromFlow();
                            }}
                            disabled={isSyncingFromFlow}
                            title="Generate invoice lines from product configuration"
                            data-testid="sync-from-flow-btn"
                        >
                            <RefreshCw className={`h-3 w-3 mr-1 ${isSyncingFromFlow ? 'animate-spin' : ''}`} />
                            {isSyncingFromFlow ? 'Syncing...' : 'Sync from Config'}
                        </Button>
                    )}
                    {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-slate-400" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-slate-400" />
                    )}
                </div>
            </div>
            
            {/* Expanded content */}
            {isExpanded && (
                <div className="p-3 bg-white">
                    {/* Line items table */}
                    {lineCount > 0 ? (
                        <table className="w-full text-xs mb-3">
                            <thead>
                                <tr className="border-b border-slate-200">
                                    <th className="text-left py-1.5 text-[10px] font-medium text-slate-500 uppercase w-8"></th>
                                    <th className="text-left py-1.5 text-[10px] font-medium text-slate-500 uppercase">Description</th>
                                    <th className="text-right py-1.5 text-[10px] font-medium text-slate-500 uppercase w-16">Qty</th>
                                    <th className="text-right py-1.5 text-[10px] font-medium text-slate-500 uppercase w-20">Unit Price</th>
                                    <th className="text-right py-1.5 text-[10px] font-medium text-slate-500 uppercase w-24">Total</th>
                                    <th className="text-right py-1.5 text-[10px] font-medium text-slate-500 uppercase w-20">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {invoiceLines.map((line, idx) => (
                                    editingLineId === line.id ? (
                                        // Editing row
                                        <tr key={line.id} className="bg-blue-50">
                                            <td className="py-2"></td>
                                            <td className="py-2 pr-2">
                                                <Input
                                                    value={lineForm.description}
                                                    onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })}
                                                    className="h-7 text-xs"
                                                    placeholder="Description"
                                                />
                                                {lineForm.notes !== undefined && (
                                                    <Input
                                                        value={lineForm.notes}
                                                        onChange={(e) => setLineForm({ ...lineForm, notes: e.target.value })}
                                                        className="h-6 text-[10px] mt-1"
                                                        placeholder="Notes (optional)"
                                                    />
                                                )}
                                            </td>
                                            <td className="py-2">
                                                <Input
                                                    type="number"
                                                    value={lineForm.quantity}
                                                    onChange={(e) => setLineForm({ ...lineForm, quantity: parseFloat(e.target.value) || 0 })}
                                                    className="h-7 text-xs w-16 text-right"
                                                />
                                            </td>
                                            <td className="py-2">
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={lineForm.unit_price}
                                                    onChange={(e) => setLineForm({ ...lineForm, unit_price: parseFloat(e.target.value) || 0 })}
                                                    className="h-7 text-xs w-20 text-right"
                                                />
                                            </td>
                                            <td className="py-2 text-right text-xs font-medium">
                                                {(lineForm.quantity * lineForm.unit_price).toLocaleString('da-DK', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="py-2 text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-6 w-6 p-0"
                                                        onClick={() => { setEditingLineId(null); resetForm(); }}
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        className="h-6 px-2 text-xs"
                                                        onClick={handleUpdateLine}
                                                        disabled={isSaving}
                                                    >
                                                        Save
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        // Display row
                                        <tr key={line.id} className="hover:bg-slate-50">
                                            <td className="py-1.5">
                                                {!isLocked && (
                                                    <div className="flex flex-col gap-0.5">
                                                        <button
                                                            className="text-slate-300 hover:text-slate-500 disabled:opacity-30"
                                                            onClick={() => handleMoveUp(idx)}
                                                            disabled={idx === 0}
                                                        >
                                                            <ChevronUp className="h-3 w-3" />
                                                        </button>
                                                        <button
                                                            className="text-slate-300 hover:text-slate-500 disabled:opacity-30"
                                                            onClick={() => handleMoveDown(idx)}
                                                            disabled={idx === invoiceLines.length - 1}
                                                        >
                                                            <ChevronDown className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="py-1.5 align-top">
                                                {/* `whitespace-pre-line` preserves the newlines
                                                    in bundle descriptions so the invoice reads:
                                                        Line 1: order title (e.g. "PDF test")
                                                        Line 2+: bundle description as entered.
                                                    Without this, `\n` collapses to a single space
                                                    and the spec sheet becomes an unreadable blob. */}
                                                <span className="text-xs font-medium text-slate-700 whitespace-pre-line block leading-snug">{line.description}</span>
                                                {line.notes && (
                                                    <span className="text-[10px] text-slate-400">({line.notes})</span>
                                                )}
                                            </td>
                                            <td className="py-1.5 text-right text-xs text-slate-600">
                                                <span className="inline-flex items-center gap-1">
                                                    {line.quantity}
                                                    {line.qty_source === 'order_quantity' && (
                                                        <RefreshCw className="h-2.5 w-2.5 text-blue-400" title="Auto-syncs with order quantity" />
                                                    )}
                                                </span>
                                            </td>
                                            <td className="py-1.5 text-right text-xs text-slate-600">
                                                {line.unit_price.toLocaleString('da-DK', { minimumFractionDigits: 2 })} {line.currency}
                                            </td>
                                            <td className="py-1.5 text-right text-xs font-medium text-slate-700">
                                                {line.total.toLocaleString('da-DK', { minimumFractionDigits: 2 })} {line.currency}
                                            </td>
                                            <td className="py-1.5 text-right">
                                                {!isLocked && (
                                                    <div className="flex justify-end gap-1">
                                                        <button
                                                            className="text-slate-400 hover:text-blue-600 p-1"
                                                            onClick={() => startEditing(line)}
                                                        >
                                                            <Edit2 className="h-3 w-3" />
                                                        </button>
                                                        <button
                                                            className="text-slate-400 hover:text-red-600 p-1"
                                                            onClick={() => handleDeleteLine(line.id)}
                                                        >
                                                            <Trash2 className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t border-slate-200 bg-slate-50">
                                    <td colSpan="4" className="py-1.5 text-right text-xs font-medium text-slate-600">Total</td>
                                    <td className="py-1.5 text-right text-xs font-bold text-slate-900">
                                        {totalAmount.toLocaleString('da-DK', { minimumFractionDigits: 2 })} {invoiceLines[0]?.currency || currency}
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    ) : (
                        <div className="text-center py-4 text-sm text-slate-400">
                            No line items yet. Click "Auto-fill" to generate from order or add manually.
                        </div>
                    )}
                    
                    {/* Add new line form */}
                    {isAdding ? (
                        <div className="p-3 bg-blue-50 rounded-lg mb-2">
                            <div className="grid grid-cols-12 gap-2 items-end">
                                <div className="col-span-5">
                                    <label className="text-[10px] text-slate-500 uppercase block mb-1">Description</label>
                                    <Input
                                        value={lineForm.description}
                                        onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })}
                                        className="h-7 text-xs"
                                        placeholder="Line item description"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="text-[10px] text-slate-500 uppercase block mb-1">Qty</label>
                                    <Input
                                        type="number"
                                        value={lineForm.quantity}
                                        onChange={(e) => setLineForm({ ...lineForm, quantity: parseFloat(e.target.value) || 0 })}
                                        className="h-7 text-xs"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="text-[10px] text-slate-500 uppercase block mb-1">Unit Price</label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={lineForm.unit_price}
                                        onChange={(e) => setLineForm({ ...lineForm, unit_price: parseFloat(e.target.value) || 0 })}
                                        className="h-7 text-xs"
                                    />
                                </div>
                                <div className="col-span-3 flex gap-1 justify-end">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 text-xs"
                                        onClick={() => { setIsAdding(false); resetForm(); }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        size="sm"
                                        className="h-7 text-xs"
                                        onClick={handleAddLine}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Adding...' : 'Add'}
                                    </Button>
                                </div>
                            </div>
                            <div className="mt-2">
                                <Input
                                    value={lineForm.notes}
                                    onChange={(e) => setLineForm({ ...lineForm, notes: e.target.value })}
                                    className="h-6 text-[10px]"
                                    placeholder="Notes (optional)"
                                />
                            </div>
                        </div>
                    ) : !isLocked && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs w-full"
                            onClick={() => setIsAdding(true)}
                        >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Line Item
                        </Button>
                    )}
                    
                    {/* Info text */}
                    <p className="text-[10px] text-slate-400 mt-2">
                        These lines appear in the Invoice Basis document. e-conomic will receive a single total line.
                    </p>
                </div>
            )}
        </div>
    );
};

// Timeline step component
const TimelineStep = ({ icon: Icon, title, status, isActive, isLast }) => {
    const getStatusColor = () => {
        if (status === 'complete') return 'bg-emerald-500 text-white';
        if (status === 'active') return 'bg-blue-500 text-white';
        return 'bg-slate-200 text-slate-400';
    };
    
    return (
        <div className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor()}`}>
                <Icon className="h-4 w-4" />
            </div>
            <div className="ml-2 mr-4">
                <p className={`text-xs font-medium ${status === 'pending' ? 'text-slate-400' : 'text-slate-700'}`}>
                    {title}
                </p>
            </div>
            {!isLast && (
                <ArrowRight className={`h-4 w-4 ${status === 'complete' ? 'text-emerald-500' : 'text-slate-300'}`} />
            )}
        </div>
    );
};

// ============================================================
// UNIFIED PURCHASE CARD COMPONENT
// Used for both Artwork/Repro Purchase and Production Purchase
// 
// STRUCTURAL IDENTITY: Both card types render the EXACT same DOM
// structure with IDENTICAL typography. Only data differs.
//
// ACCENT COLORS: Limited to badges and icons ONLY.
// All text (labels, values, totals) uses neutral slate colors.
//
// SECTION ORDER (always rendered in this order):
// 1. HEADER: Icon, Title, PO Badge, Status Badge, Edit Button
// 2. META: Supplier, Currency, Linked To (3-column grid always)
// 3. TABLE: Always rendered (single-row or multi-row)
// 4. ACTIONS: Status actions, booking buttons
// 5. FOOTER: Locked indicator
// ============================================================
const PurchaseCard = ({
    title,
    icon: IconComponent,
    iconColor = 'text-slate-500',
    badgeColor = 'amber', // 'amber' | 'purple' - ONLY for PO badge
    poNumber,
    status,
    isLocked,
    // Data display
    supplier,
    currency = 'EUR',
    totalAmount,
    lineItems = [], // Array of { name, sku?, quantity, unit?, unit_price, line_total }
    linkedTo,
    linkedToLabel,
    // Edit configuration
    editableQty = false, // When true, qty is also editable (for production)
    // Edit state
    isEditing,
    setIsEditing,
    // Edit handlers
    onSave,
    onCancel,
    isSaving,
    // Line items edit state
    lineItemsForm,
    setLineItemsForm,
    // Status actions
    statusActions = [],
    // Translation
    t = (key, fallback) => fallback,
    // BOM Breakdown (for vinyl orders)
    bomBreakdown = null,
    isBomExpanded = false,
    setIsBomExpanded = null,
    isRefreshingBom = false,
    onRefreshBom = null,
}) => {
    // Badge colors (ONLY place accent colors are used)
    const badgeClasses = {
        amber: 'bg-amber-100 text-amber-700',
        purple: 'bg-purple-100 text-purple-700',
    };
    const poBadgeClass = badgeClasses[badgeColor] || badgeClasses.amber;
    
    // Calculate total for display
    const hasItems = lineItems && lineItems.length > 0;
    const displayTotal = isEditing && lineItemsForm 
        ? lineItemsForm.reduce((sum, i) => sum + ((parseFloat(i.unit_price) || 0) * (parseFloat(i.quantity) || 0)), 0)
        : totalAmount;
    
    return (
        <div className={`rounded-lg border ${isLocked ? 'border-slate-300 bg-background' : 'border-slate-300 bg-white'} overflow-hidden flex flex-col`}>
            {/* ═══════════════════════════════════════════════════════════
                SECTION 1: HEADER (compact) with actions
               ═══════════════════════════════════════════════════════════ */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                <div className="flex items-center gap-2 flex-wrap">
                    <IconComponent className={`h-4 w-4 ${iconColor} flex-shrink-0`} />
                    <span className="text-sm font-semibold text-slate-700">{title}</span>
                    {poNumber && (
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded font-medium ${poBadgeClass}`}>
                            PO #{poNumber}
                        </span>
                    )}
                    {status && <FinanceStatusBadge status={status} />}
                </div>
                {/* Actions in header */}
                <div className="flex items-center gap-1">
                    {!isLocked && !isEditing && statusActions.map((action, idx) => (
                        <Button
                            key={idx}
                            size="sm"
                            variant={action.variant || 'ghost'}
                            onClick={action.onClick}
                            disabled={action.disabled || isSaving}
                            className="h-6 text-xs px-2 text-slate-600"
                        >
                            {action.icon && <action.icon className="h-3 w-3 mr-1" />}
                            {action.label}
                        </Button>
                    ))}
                    {!isLocked && !isEditing && setIsEditing && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-slate-600 px-2"
                            onClick={() => setIsEditing(true)}
                        >
                            <Edit2 className="h-3 w-3 mr-1" />
                            Edit
                        </Button>
                    )}
                </div>
            </div>
            
            {/* ═══════════════════════════════════════════════════════════
                SECTION 2: META (compact inline)
               ═══════════════════════════════════════════════════════════ */}
            <div className="px-3 py-1.5 border-b border-slate-100 bg-background/50">
                <div className="flex items-center gap-6 text-xs">
                    <div className="flex items-center gap-1">
                        <span className="text-slate-400">Supplier:</span>
                        <span className="font-medium text-slate-700">{supplier || '—'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="text-slate-400">Currency:</span>
                        <span className="font-medium text-slate-700">{currency}</span>
                    </div>
                    {(linkedToLabel || linkedTo) && (
                        <div className="flex items-center gap-1">
                            <span className="text-slate-400">Linked to:</span>
                            <span className="font-medium text-slate-700">{linkedToLabel || linkedTo}</span>
                        </div>
                    )}
                </div>
            </div>
            
            {/* ═══════════════════════════════════════════════════════════
                SECTION 3: TABLE (compact)
               ═══════════════════════════════════════════════════════════ */}
            <div className="px-3 py-2 flex-1">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b border-slate-200">
                            <th className="text-left py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide">Item</th>
                            <th className="text-right py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide w-16">Qty</th>
                            <th className="text-right py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide w-20">Unit Price</th>
                            <th className="text-right py-1.5 text-[10px] font-medium text-slate-500 uppercase tracking-wide w-20">Total</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {!hasItems && !isEditing && (
                            <tr>
                                <td colSpan="4" className="py-2 text-center text-xs text-slate-400">
                                    No items yet
                                </td>
                            </tr>
                        )}
                        {isEditing && lineItemsForm ? (
                            lineItemsForm.map((item, idx) => (
                                <tr key={item.price_item_id || item.id || idx}>
                                    <td className="py-1.5">
                                        <span className="text-xs font-medium text-slate-700">{item.name}</span>
                                        {item.sku && <span className="text-[10px] text-slate-400 ml-1">({item.sku})</span>}
                                    </td>
                                    <td className="py-1.5 text-right">
                                        {editableQty ? (
                                            <Input
                                                type="number"
                                                min="0"
                                                value={item.quantity}
                                                onChange={(e) => {
                                                    const updated = [...lineItemsForm];
                                                    updated[idx] = { ...updated[idx], quantity: e.target.value };
                                                    setLineItemsForm(updated);
                                                }}
                                                className="w-16 h-6 text-right text-xs"
                                            />
                                        ) : (
                                            <span className="text-xs text-slate-600">{item.quantity} {item.unit || ''}</span>
                                        )}
                                    </td>
                                    <td className="py-1.5 text-right">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={item.unit_price}
                                            onChange={(e) => {
                                                const updated = [...lineItemsForm];
                                                updated[idx] = { ...updated[idx], unit_price: e.target.value };
                                                setLineItemsForm(updated);
                                            }}
                                            className="w-20 h-6 text-right text-xs"
                                        />
                                    </td>
                                    <td className="py-1.5 text-right text-xs font-medium text-slate-700">
                                        {((parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0)).toFixed(2)}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            hasItems && lineItems.map((item, idx) => (
                                <tr key={item.price_item_id || item.id || idx}>
                                    <td className="py-1.5">
                                        <span className="text-xs font-medium text-slate-700">{item.name}</span>
                                        {item.sku && <span className="text-[10px] text-slate-400 ml-1">({item.sku})</span>}
                                    </td>
                                    <td className="py-1.5 text-right text-xs text-slate-600">
                                        {item.quantity} {item.unit || ''}
                                    </td>
                                    <td className="py-1.5 text-right text-xs text-slate-600">
                                        {item.unit_price?.toFixed(2) || '—'}
                                    </td>
                                    <td className="py-1.5 text-right text-xs font-medium text-slate-700">
                                        {item.line_total?.toFixed(2) || '—'}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                    <tfoot>
                        <tr className="border-t border-slate-200">
                            <td colSpan="3" className="py-1.5 text-right text-xs font-medium text-slate-600">Total</td>
                            <td className="py-1.5 text-right text-xs font-bold text-slate-900">
                                {displayTotal?.toFixed(2) || '0.00'} {currency}
                            </td>
                        </tr>
                    </tfoot>
                </table>
                
                {/* Edit buttons (shown when editing) */}
                {isEditing && (
                    <div className="flex justify-end gap-2 pt-2 mt-2 border-t border-slate-100">
                        <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button size="sm" className="h-6 text-xs px-3" onClick={onSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </div>
                )}
            </div>
            
            {/* ═══════════════════════════════════════════════════════════
                SECTION 3.5: BOM BREAKDOWN (vinyl orders only)
               ═══════════════════════════════════════════════════════════ */}
            {bomBreakdown && bomBreakdown.components?.length > 0 && (
                <div className="border-t border-slate-100">
                    {/* Expandable header */}
                    <button
                        onClick={() => setIsBomExpanded && setIsBomExpanded(!isBomExpanded)}
                        className="w-full flex items-center justify-between px-3 py-1.5 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                    >
                        <div className="flex items-center gap-1.5 text-xs">
                            <Package className="h-3.5 w-3.5 text-slate-400" />
                            <span className="font-medium text-slate-600">Cost Breakdown</span>
                            <span className="text-slate-400">({bomBreakdown.components.length} components)</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Refresh button inline */}
                            {onRefreshBom && (bomBreakdown.components?.some(c => c.error) || bomBreakdown.source === 'persisted_snapshot') && (
                                <span
                                    onClick={(e) => { e.stopPropagation(); onRefreshBom(); }}
                                    className="text-[10px] text-blue-600 hover:text-blue-700 cursor-pointer flex items-center gap-0.5"
                                >
                                    {isRefreshingBom ? (
                                        <><Loader2 className="h-3 w-3 animate-spin" /></>
                                    ) : (
                                        <><RefreshCw className="h-3 w-3" /> Refresh</>
                                    )}
                                </span>
                            )}
                            {isBomExpanded ? (
                                <ChevronUp className="h-3.5 w-3.5 text-slate-400" />
                            ) : (
                                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                            )}
                        </div>
                    </button>
                    
                    {/* Expanded BOM table */}
                    {isBomExpanded && (
                        <div className="px-3 pb-2">
                            <table className="w-full text-[11px]">
                                <thead>
                                    <tr className="border-b border-slate-100">
                                        <th className="text-left py-1 text-[10px] font-medium text-slate-400 uppercase">Component</th>
                                        <th className="text-left py-1 text-[10px] font-medium text-slate-400 uppercase w-24">SKU</th>
                                        <th className="text-right py-1 text-[10px] font-medium text-slate-400 uppercase w-14">Qty</th>
                                        <th className="text-right py-1 text-[10px] font-medium text-slate-400 uppercase w-16">Unit</th>
                                        <th className="text-right py-1 text-[10px] font-medium text-slate-400 uppercase w-16">Line</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {bomBreakdown.components.map((comp, idx) => (
                                        <tr key={idx} className={comp.error ? 'bg-red-50/30' : ''}>
                                            <td className="py-1">
                                                <span className={`font-medium ${comp.error ? 'text-red-600' : 'text-slate-700'}`}>
                                                    {comp.name || comp.category}
                                                </span>
                                                {comp.is_fixed_cost && (
                                                    <span className="ml-1 text-[9px] px-1 py-0.5 bg-amber-100 text-amber-600 rounded">
                                                        Fixed
                                                    </span>
                                                )}
                                                {comp.error && (
                                                    <span className="ml-1 text-[9px] text-red-500">
                                                        (not found)
                                                    </span>
                                                )}
                                            </td>
                                            <td className="py-1 text-slate-400 font-mono text-[9px]">
                                                {comp.sku || '—'}
                                            </td>
                                            <td className="py-1 text-right text-slate-600">
                                                {comp.quantity?.toLocaleString() || '—'}
                                            </td>
                                            <td className="py-1 text-right text-slate-600">
                                                {comp.cost_per_unit != null ? `€${comp.cost_per_unit.toFixed(4)}` : '—'}
                                            </td>
                                            <td className="py-1 text-right font-medium text-slate-700">
                                                {comp.line_cost != null ? `€${comp.line_cost.toFixed(2)}` : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="border-t border-slate-200">
                                    <tr>
                                        <td colSpan="4" className="py-1 text-right text-[10px] font-medium text-slate-500">
                                            Total BOM Cost
                                        </td>
                                        <td className="py-1 text-right text-xs font-bold text-slate-800">
                                            €{bomBreakdown.total_cost?.toFixed(2)}
                                        </td>
                                    </tr>
                                    <tr className="text-[10px]">
                                        <td colSpan="4" className="text-right text-slate-400">
                                            Cost Per Unit ({bomBreakdown.quantity?.toLocaleString()} pcs)
                                        </td>
                                        <td className="text-right text-slate-500">
                                            €{bomBreakdown.cost_per_unit?.toFixed(4)}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                            <div className="text-[9px] text-slate-400 mt-1">
                                {bomBreakdown.source === 'persisted_snapshot' 
                                    ? 'Snapshot from order submission'
                                    : bomBreakdown.source === 'refreshed_calculation'
                                    ? 'Refreshed from current BOM data'
                                    : 'Live calculation'
                                }
                            </div>
                        </div>
                    )}
                </div>
            )}
            
            {/* ═══════════════════════════════════════════════════════════
                SECTION 4: FOOTER (only show if locked)
               ═══════════════════════════════════════════════════════════ */}
            {isLocked && (
                <div className="px-3 py-1.5 border-t border-slate-100 bg-background/50">
                    <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        <Lock className="h-3 w-3" />
                        <span>Prices locked</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export function FinanceSection({ order, onUpdate, embedded = false, artworkJob = null }) {
    const { t } = useTranslation();
    const [finance, setFinance] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditingPurchase, setIsEditingPurchase] = useState(false);
    const [isEditingInvoice, setIsEditingInvoice] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isBooking, setIsBooking] = useState(false);
    const [showEvents, setShowEvents] = useState(false);
    // Production Purchase PDF download (email-to-supplier PO)
    const [isDownloadingPurchasePdf, setIsDownloadingPurchasePdf] = useState(false);
    // ── Supplier RFQ state ─────────────────────────────────────
    // Keeps the Send-RFQ confirmation dialog isolated from the rest of
    // the Finance state. Opening the dialog triggers a GET to
    // `/orders/{id}/production-purchase/rfq/defaults` so the recipient
    // / subject / message come pre-filled from the resolved supplier
    // partner. The dialog is admin-only; all gating lives on the
    // backend route.
    const [rfqDialogOpen, setRfqDialogOpen] = useState(false);
    const [rfqLoading, setRfqLoading] = useState(false);
    const [rfqSending, setRfqSending] = useState(false);
    const [rfqDefaults, setRfqDefaults] = useState(null);
    const [rfqRecipient, setRfqRecipient] = useState('');
    const [rfqSubject, setRfqSubject] = useState('');
    const [rfqMessage, setRfqMessage] = useState('');
    const [rfqCc, setRfqCc] = useState('');
    // ── RFQ history dialog (read-only inspection of previous sends) ─
    const [rfqHistoryOpen, setRfqHistoryOpen] = useState(false);
    // ── Mark Supplier Quote Received dialog ─────────────────────────
    // Explicit admin confirmation that the supplier reply landed. The
    // button is gated by the client-side outstanding-lines check, but
    // we re-fetch the server's authoritative preview when the dialog
    // opens so a stale UI can't bypass the gate.
    const [markReceivedOpen, setMarkReceivedOpen] = useState(false);
    const [markReceivedPreview, setMarkReceivedPreview] = useState(null);
    const [markReceivedLoading, setMarkReceivedLoading] = useState(false);
    const [markReceivedSubmitting, setMarkReceivedSubmitting] = useState(false);
    
    // Proforma/estimated revenue state
    const [proforma, setProforma] = useState(null);
    const [isEditingProforma, setIsEditingProforma] = useState(false);
    const [proformaForm, setProformaForm] = useState({ unit_price: '' });
    
    // Unified finance summary (BOM-derived costs, aggregated view)
    const [financeSummary, setFinanceSummary] = useState(null);
    
    // Artwork/Repro supplier purchase state
    const [artworkPurchase, setArtworkPurchase] = useState(null);
    const [isEditingArtworkPurchase, setIsEditingArtworkPurchase] = useState(false);
    const [artworkPurchaseForm, setArtworkPurchaseForm] = useState({ line_items: [] });
    
    // Manual supplier purchases state
    const [manualPurchases, setManualPurchases] = useState([]);
    const [showAddPurchaseModal, setShowAddPurchaseModal] = useState(false);
    const [editingPurchaseId, setEditingPurchaseId] = useState(null);
    const [partners, setPartners] = useState([]);
    const [catalogs, setCatalogs] = useState([]);
    const [catalogItems, setCatalogItems] = useState([]);
    const [selectedCatalog, setSelectedCatalog] = useState(null);
    const [newPurchaseForm, setNewPurchaseForm] = useState({
        partner_id: '',
        currency: 'EUR',
        description: '',
        notes: '',
        line_items: []
    });
    const [newLineItem, setNewLineItem] = useState({
        name: '',
        sku: '',
        unit: 'piece',
        quantity: 1,
        unit_price: 0,
        price_item_id: null
    });
    
    // Purchase edit form
    const [purchaseForm, setPurchaseForm] = useState({
        quantity: '',
        unit_price: '',
        currency: 'EUR',
    });
    
    // Invoice edit form
    const [invoiceForm, setInvoiceForm] = useState({
        quantity: '',
        unit_price: '',
        currency: 'EUR',
    });
    
    // Invoice line items (Invoice Basis)
    const [invoiceLines, setInvoiceLines] = useState([]);
    
    // PO Line Items (unified production purchase)
    const [poLines, setPoLines] = useState([]);
    const [poTotals, setPoTotals] = useState(null);
    const [isBomExpanded, setIsBomExpanded] = useState(false);
    const [isRefreshingBom, setIsRefreshingBom] = useState(false);
    const [isAddingLine, setIsAddingLine] = useState(false);
    const [newLineForm, setNewLineForm] = useState({ description: '', quantity: 1, unit_price: 0 });
    const [editingLineId, setEditingLineId] = useState(null);
    const [editLineForm, setEditLineForm] = useState({ quantity: 0, unit_price: 0 });

    useEffect(() => {
        if (order?.id) {
            fetchFinance();
            fetchManualPurchases();
            fetchInvoiceLines();
            // Fetch PO lines (replaces old BOM breakdown)
            fetchPoLines();
        }
    }, [order?.id]);

    // Fetch artwork/repro supplier purchase
    useEffect(() => {
        if (artworkJob?.supplier_purchase_id) {
            fetchArtworkPurchase();
        }
    }, [artworkJob?.supplier_purchase_id]);

    // Fetch partners and catalogs when modal opens
    useEffect(() => {
        if (showAddPurchaseModal) {
            fetchPartners();
            fetchCatalogs();
        }
    }, [showAddPurchaseModal]);

    const fetchPartners = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/partners`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPartners(response.data || []);
        } catch (error) {
            console.error('Failed to fetch partners:', error);
        }
    };

    const fetchCatalogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/pricing/catalogs`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCatalogs(response.data || []);
        } catch (error) {
            console.error('Failed to fetch catalogs:', error);
        }
    };

    const fetchCatalogItems = async (catalogId) => {
        if (!catalogId) {
            setCatalogItems([]);
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/pricing/items`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { catalog_id: catalogId, limit: 500 },
                }
            );
            const items = Array.isArray(response.data) ? response.data : (response.data?.items || []);
            setCatalogItems(items);
        } catch (error) {
            console.error('Failed to fetch catalog items:', error);
            setCatalogItems([]);
        }
    };

    const fetchManualPurchases = async () => {
        if (!order?.id) return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/orders/${order.id}/supplier-purchases`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Filter to only manual purchases (exclude artwork purchases which are shown separately)
            const manual = (response.data || []).filter(p => p.purchase_type === 'manual');
            setManualPurchases(manual);
        } catch (error) {
            console.error('Failed to fetch manual purchases:', error);
        }
    };
    
    // Fetch invoice line items (Invoice Basis)
    const fetchInvoiceLines = async () => {
        if (!order?.id) return;
        try {
            const response = await financeApi.getInvoiceLines(order.id);
            setInvoiceLines(response.data || []);
        } catch (error) {
            console.error('Failed to fetch invoice lines:', error);
            setInvoiceLines([]);
        }
    };

    const fetchArtworkPurchase = async () => {
        if (!artworkJob?.supplier_purchase_id) return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/supplier-purchases/${artworkJob.supplier_purchase_id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setArtworkPurchase(response.data);
            // Initialize edit form with line items
            if (response.data.line_items) {
                setArtworkPurchaseForm({ 
                    line_items: response.data.line_items.map(item => ({
                        ...item,
                        unit_price: item.unit_price?.toString() || '0'
                    }))
                });
            }
        } catch (error) {
            console.error('Failed to fetch artwork purchase:', error);
        }
    };

    // Fetch PO Line Items (unified production purchase)
    const fetchPoLines = async () => {
        if (!order?.id) return;
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/orders/${order.id}/po-lines`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPoLines(response.data.lines || []);
            setPoTotals(response.data.totals || null);
        } catch (error) {
            console.error('Failed to fetch PO lines:', error);
        }
    };
    
    // Refresh BOM lines from current procurement data
    const refreshBomLines = async () => {
        if (!order?.id) return;
        setIsRefreshingBom(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE}/api/orders/${order.id}/po-lines/sync-from-bom`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPoLines(response.data.lines || []);
            setPoTotals(response.data.totals || null);
            toast.success('BOM lines refreshed');
            // Also refresh finance data
            fetchFinance();
        } catch (error) {
            console.error('Failed to refresh BOM:', error);
            toast.error('Failed to refresh BOM lines');
        } finally {
            setIsRefreshingBom(false);
        }
    };
    
    // Add a new PO line (adjustment for vinyl, manual for non-vinyl)
    const handleAddPoLine = async () => {
        if (!newLineForm.description || newLineForm.quantity <= 0) {
            toast.error('Please enter description and quantity');
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE}/api/orders/${order.id}/po-lines`,
                {
                    description: newLineForm.description,
                    quantity: parseFloat(newLineForm.quantity),
                    unit_price: parseFloat(newLineForm.unit_price) || 0,
                    line_type: order?.product_type === 'vinyl' ? 'adjustment' : 'manual'
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Refresh lines
            await fetchPoLines();
            setIsAddingLine(false);
            setNewLineForm({ description: '', quantity: 1, unit_price: 0 });
            toast.success('Line added');
            // Refresh finance
            fetchFinance();
        } catch (error) {
            console.error('Failed to add line:', error);
            toast.error(error.response?.data?.detail || 'Failed to add line');
        }
    };
    
    // Update a PO line (override BOM price/qty or edit adjustment/manual)
    const handleUpdatePoLine = async (lineId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE}/api/orders/${order.id}/po-lines/${lineId}`,
                { 
                    quantity: parseFloat(editLineForm.quantity) || 0,
                    unit_price: parseFloat(editLineForm.unit_price) || 0 
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Refresh lines
            await fetchPoLines();
            setEditingLineId(null);
            setEditLineForm({ quantity: 0, unit_price: 0 });
            toast.success('Line updated');
            // Refresh finance
            fetchFinance();
        } catch (error) {
            console.error('Failed to update line:', error);
            toast.error('Failed to update line');
        }
    };
    
    // Delete a PO line (only adjustment/manual, not BOM)
    const handleDeletePoLine = async (lineId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE}/api/orders/${order.id}/po-lines/${lineId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Refresh lines
            await fetchPoLines();
            toast.success('Line deleted');
            // Refresh finance
            fetchFinance();
        } catch (error) {
            console.error('Failed to delete line:', error);
            toast.error(error.response?.data?.detail || 'Failed to delete line');
        }
    };
    
    // Reset an overridden BOM line to original price
    const handleResetOverride = async (lineId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE}/api/orders/${order.id}/po-lines/${lineId}/reset-override`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Refresh lines
            await fetchPoLines();
            toast.success('Reset to original price');
            // Refresh finance
            fetchFinance();
        } catch (error) {
            console.error('Failed to reset override:', error);
            toast.error('Failed to reset override');
        }
    };

    const fetchFinance = async () => {
        try {
            const response = await financeApi.getOrderFinance(order.id);
            setFinance(response.data);
            
            // Set proforma data
            if (response.data.proforma) {
                setProforma(response.data.proforma);
                setProformaForm({
                    unit_price: response.data.proforma.unit_price?.toString() || ''
                });
            }
            
            // Populate forms
            if (response.data.purchase) {
                setPurchaseForm({
                    quantity: response.data.purchase.quantity?.toString() || '',
                    unit_price: response.data.purchase.unit_price?.toString() || '',
                    currency: response.data.purchase.currency || 'EUR',
                });
            }
            if (response.data.invoice) {
                setInvoiceForm({
                    quantity: response.data.invoice.quantity?.toString() || '',
                    unit_price: response.data.invoice.unit_price?.toString() || '',
                    currency: response.data.invoice.currency || 'EUR',
                });
            }
            
            // Also fetch unified finance summary for BOM-derived costs
            try {
                const summaryResponse = await financeApi.getFinanceSummary(order.id);
                setFinanceSummary(summaryResponse.data);
            } catch (summaryError) {
                // Finance summary endpoint might not have data yet
                console.log('Finance summary not available yet');
            }
        } catch (error) {
            if (error.response?.status !== 404) {
                console.error('Failed to fetch finance data');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Save proforma changes
    const handleSaveProforma = async () => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${API_BASE}/api/orders/${order.id}/finance/proforma`,
                { unit_price: parseFloat(proformaForm.unit_price) || 0 },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Estimated revenue updated');
            setIsEditingProforma(false);
            await fetchFinance();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save');
        } finally {
            setIsSaving(false);
        }
    };

    // Determine timeline status
    const getTimelineStatus = () => {
        const purchase = finance?.purchase;
        const invoice = finance?.invoice;
        const confirmation = order?.supplier_confirmation;
        
        return {
            purchase: purchase?.economic_booked ? 'complete' : purchase ? 'active' : 'pending',
            dispatch: confirmation?.dispatch_delivery_note_number ? 'complete' : 'pending',
            delivery: confirmation?.delivery_notice_sent_at ? 'complete' : 'pending',
            invoice: invoice?.economic_booked ? 'complete' : invoice ? 'active' : 'pending',
        };
    };

    // Save purchase changes
    const handleSavePurchase = async () => {
        setIsSaving(true);
        try {
            const data = {
                quantity: purchaseForm.quantity ? parseInt(purchaseForm.quantity) : null,
                unit_price: purchaseForm.unit_price ? parseFloat(purchaseForm.unit_price) : null,
                currency: purchaseForm.currency,
            };
            await financeApi.updatePurchase(order.id, data);
            toast.success(t('finance.purchase_saved', 'Purchase updated'));
            setIsEditingPurchase(false);
            await fetchFinance();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('finance.save_failed', 'Failed to save'));
        } finally {
            setIsSaving(false);
        }
    };

    // Save artwork/repro purchase line item prices (admin only)
    const handleSaveArtworkPurchase = async () => {
        if (!artworkPurchase?.id) return;
        
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            
            // Calculate new total from edited line items
            const updatedLineItems = artworkPurchaseForm.line_items.map(item => ({
                ...item,
                unit_price: parseFloat(item.unit_price) || 0,
                line_total: (parseFloat(item.unit_price) || 0) * item.quantity
            }));
            
            const totalAmount = updatedLineItems.reduce((sum, item) => sum + item.line_total, 0);
            
            await axios.patch(
                `${API_BASE}/api/supplier-purchases/${artworkPurchase.id}`,
                {
                    line_items: updatedLineItems,
                    total_amount: totalAmount
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success('Artwork purchase prices updated');
            setIsEditingArtworkPurchase(false);
            await fetchArtworkPurchase();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save artwork purchase');
        } finally {
            setIsSaving(false);
        }
    };

    // Update artwork purchase status
    const handleUpdateArtworkPurchaseStatus = async (newStatus) => {
        if (!artworkPurchase?.id) return;
        
        const confirmMsg = newStatus === 'Ordered' 
            ? 'Mark this artwork purchase as Ordered? Prices will remain editable until booked.'
            : `Change status to ${newStatus}? This may lock pricing fields.`;
            
        if (!window.confirm(confirmMsg)) return;
        
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${API_BASE}/api/supplier-purchases/${artworkPurchase.id}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(`Artwork purchase status updated to ${newStatus}`);
            await fetchArtworkPurchase();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update status');
        } finally {
            setIsSaving(false);
        }
    };

    // Save invoice changes
    const handleSaveInvoice = async () => {
        setIsSaving(true);
        try {
            const data = {
                quantity: invoiceForm.quantity ? parseInt(invoiceForm.quantity) : null,
                unit_price: invoiceForm.unit_price ? parseFloat(invoiceForm.unit_price) : null,
                currency: invoiceForm.currency,
            };
            await financeApi.updateInvoice(order.id, data);
            toast.success(t('finance.invoice_saved', 'Invoice updated'));
            setIsEditingInvoice(false);
            await fetchFinance();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('finance.save_failed', 'Failed to save'));
        } finally {
            setIsSaving(false);
        }
    };

    // Book invoice to e-conomic
    const handleBookInvoice = async () => {
        if (!window.confirm(t('finance.book_invoice_confirm', 'Book this invoice to e-conomic? This will freeze customer data and lock the invoice.'))) {
            return;
        }
        
        setIsBooking(true);
        try {
            await financeApi.bookInvoice(order.id);
            toast.success(t('finance.invoice_booked', 'Invoice booked to e-conomic'));
            await fetchFinance();
            if (onUpdate) onUpdate();
        } catch (error) {
            // Backend now returns a structured error with a unique error_id
            // when an unhandled exception occurs. Show it so the user can
            // quote it in a support ticket (matches booking_errors doc).
            const d = error.response?.data?.detail;
            let msg;
            if (d && typeof d === 'object') {
                const parts = [d.message || t('finance.book_failed', 'Failed to book')];
                if (d.stage) parts.push(`Stage: ${d.stage}`);
                if (d.error_id) parts.push(`Error ID: ${d.error_id}`);
                msg = parts.join(' · ');
                // Also copy the error_id to clipboard automatically for
                // frictionless bug reports.
                if (d.error_id && navigator?.clipboard?.writeText) {
                    try { navigator.clipboard.writeText(d.error_id); } catch (e) { /* non-fatal */ }
                }
            } else {
                msg = d || error.message || t('finance.book_failed', 'Failed to book');
            }
            toast.error(msg, { duration: 12000 });
        } finally {
            setIsBooking(false);
        }
    };

    // ==================== MANUAL PURCHASE FUNCTIONS ====================
    
    const handleAddLineItem = () => {
        if (!newLineItem.name || newLineItem.quantity <= 0) {
            toast.error('Please enter item name and quantity');
            return;
        }
        
        const lineTotal = newLineItem.unit_price * newLineItem.quantity;
        const item = {
            ...newLineItem,
            line_total: lineTotal,
            _tempId: Date.now() // Temporary ID for React key
        };
        
        setNewPurchaseForm(prev => ({
            ...prev,
            line_items: [...prev.line_items, item]
        }));
        
        // Reset line item form
        setNewLineItem({
            name: '',
            sku: '',
            unit: 'piece',
            quantity: 1,
            unit_price: 0,
            price_item_id: null
        });
    };

    const handleRemoveLineItem = (index) => {
        setNewPurchaseForm(prev => ({
            ...prev,
            line_items: prev.line_items.filter((_, i) => i !== index)
        }));
    };

    const handleSelectCatalogItem = (item) => {
        // Pick cost_price from the first tier (lowest qty band) — fall back to sales/calculated price
        const firstTier = item.tiers?.[0] || {};
        const price = firstTier.cost_price ?? firstTier.unit_price ?? firstTier.sales_price ?? firstTier.calculated_sales_price ?? 0;
        
        setNewLineItem({
            name: item.name,
            sku: item.sku || '',
            unit: item.unit || 'piece',
            quantity: 1,
            unit_price: price,
            price_item_id: item.id
        });
    };

    const handleCreateManualPurchase = async () => {
        if (newPurchaseForm.line_items.length === 0) {
            toast.error('Please add at least one line item');
            return;
        }
        
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            const payload = {
                partner_id: newPurchaseForm.partner_id || null,
                currency: newPurchaseForm.currency,
                description: newPurchaseForm.description,
                notes: newPurchaseForm.notes,
                line_items: newPurchaseForm.line_items.map(item => ({
                    price_item_id: item.price_item_id,
                    name: item.name,
                    sku: item.sku,
                    unit: item.unit,
                    quantity: item.quantity,
                    unit_price: item.unit_price
                })),
            };
            
            if (editingPurchaseId) {
                await axios.patch(
                    `${API_BASE}/api/supplier-purchases/${editingPurchaseId}`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Additional cost updated');
            } else {
                await axios.post(
                    `${API_BASE}/api/supplier-purchases/manual`,
                    { order_id: order.id, ...payload },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Additional cost created');
            }
            
            setShowAddPurchaseModal(false);
            setEditingPurchaseId(null);
            resetNewPurchaseForm();
            await fetchManualPurchases();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save additional cost');
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleEditManualPurchase = (mp) => {
        if (['Invoiced', 'Paid'].includes(mp.status)) {
            toast.error('Cannot edit an invoiced or paid purchase');
            return;
        }
        setEditingPurchaseId(mp.id);
        setNewPurchaseForm({
            partner_id: mp.partner_id || '',
            currency: mp.currency || 'EUR',
            description: mp.description || '',
            notes: mp.notes || '',
            line_items: (mp.line_items || []).map(li => ({
                price_item_id: li.price_item_id || null,
                name: li.name || '',
                sku: li.sku || '',
                unit: li.unit || 'piece',
                quantity: Number(li.quantity || 1),
                unit_price: Number(li.unit_price || 0),
            })),
        });
        setShowAddPurchaseModal(true);
    };

    const handleDeleteManualPurchase = async (purchaseId, poNumber) => {
        if (!window.confirm(`Delete manual purchase PO #${poNumber}? This cannot be undone.`)) {
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE}/api/supplier-purchases/${purchaseId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success('Manual purchase deleted');
            await fetchManualPurchases();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete purchase');
        }
    };

    const handleUpdateManualPurchaseStatus = async (purchaseId, newStatus) => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${API_BASE}/api/supplier-purchases/${purchaseId}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(`Purchase status updated to ${newStatus}`);
            await fetchManualPurchases();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update status');
        } finally {
            setIsSaving(false);
        }
    };

    const resetNewPurchaseForm = () => {
        setNewPurchaseForm({
            partner_id: '',
            currency: 'EUR',
            description: '',
            notes: '',
            line_items: []
        });
        setNewLineItem({
            name: '',
            sku: '',
            unit: 'piece',
            quantity: 1,
            unit_price: 0,
            price_item_id: null
        });
        setSelectedCatalog(null);
        setCatalogItems([]);
    };

    const getNewPurchaseTotal = () => {
        return newPurchaseForm.line_items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    };

    // ==================== END MANUAL PURCHASE FUNCTIONS ====================

    const timelineStatus = getTimelineStatus();
    const purchase = finance?.purchase;
    const invoice = finance?.invoice;
    const isPurchaseBooked = purchase?.economic_booked;
    const isInvoiceBooked = invoice?.economic_booked;

    // Download the Production Purchase PDF (email-ready PO to send to the pressing plant).
    const handleDownloadPurchasePdf = async () => {
        if (!order?.id) return;
        setIsDownloadingPurchasePdf(true);
        try {
            const response = await financeApi.downloadProductionPurchasePdf(order.id);
            const contentDisposition = response.headers['content-disposition'];
            let filename = `Production_Purchase_${order.order_number || order.id}.pdf`;
            if (contentDisposition) {
                const m = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (m && m[1]) filename = m[1].replace(/['"]/g, '');
            }
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('Production Purchase PDF downloaded');
        } catch (error) {
            // Blob errors surface a Blob instead of the JSON detail — decode it.
            let detail = 'Failed to download PDF';
            try {
                const errBlob = error.response?.data;
                if (errBlob instanceof Blob) {
                    const text = await errBlob.text();
                    const parsed = JSON.parse(text);
                    if (parsed?.detail) detail = parsed.detail;
                } else if (error.response?.data?.detail) {
                    detail = error.response.data.detail;
                }
            } catch { /* fall through */ }
            toast.error(detail);
        } finally {
            setIsDownloadingPurchasePdf(false);
        }
    };

    // ── Supplier RFQ (Request For Quotation) handlers ────────────────────
    // Open the dialog, fetch suggested recipient/subject/message from the
    // backend, pre-fill the form.
    const handleOpenRfqDialog = async () => {
        if (!order?.id) return;
        setRfqDialogOpen(true);
        setRfqLoading(true);
        setRfqDefaults(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${API_BASE}/api/orders/${order.id}/production-purchase/rfq/defaults`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const data = res.data || {};
            setRfqDefaults(data);
            setRfqRecipient(data.default_recipient || '');
            setRfqSubject(data.default_subject || '');
            setRfqMessage(data.default_message || '');
            setRfqCc((data.default_cc || []).join(', '));
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to load RFQ defaults');
            setRfqDialogOpen(false);
        } finally {
            setRfqLoading(false);
        }
    };

    // Preview the RFQ PDF in a new tab without sending.
    const handlePreviewRfqPdf = async () => {
        if (!order?.id) return;
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${API_BASE}/api/orders/${order.id}/production-purchase/rfq/pdf`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                },
            );
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
            // Revoke later so the new tab has time to render.
            setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            let detail = 'Failed to generate RFQ preview';
            try {
                const errBlob = err.response?.data;
                if (errBlob instanceof Blob) {
                    const text = await errBlob.text();
                    const parsed = JSON.parse(text);
                    if (parsed?.detail) detail = parsed.detail;
                } else if (err.response?.data?.detail) {
                    detail = err.response.data.detail;
                }
            } catch { /* fall through */ }
            toast.error(detail);
        }
    };

    // Actually send the RFQ email.
    const handleSendRfq = async () => {
        if (!order?.id) return;
        const recipient = (rfqRecipient || '').trim();
        if (!recipient || !recipient.includes('@')) {
            toast.error('Please enter a valid recipient email.');
            return;
        }
        setRfqSending(true);
        try {
            const token = localStorage.getItem('token');
            // Always send an explicit cc array (even empty) so a user who
            // removed every CC isn't silently re-defaulted server-side.
            const ccList = (rfqCc || '').split(',').map(e => e.trim()).filter(Boolean);
            const invalidCc = ccList.filter(e => !e.includes('@'));
            if (invalidCc.length > 0) {
                toast.error(`Invalid CC email(s): ${invalidCc.join(', ')}`);
                setRfqSending(false);
                return;
            }
            await axios.post(
                `${API_BASE}/api/orders/${order.id}/production-purchase/rfq/send`,
                {
                    recipient,
                    subject: (rfqSubject || '').trim() || undefined,
                    message: (rfqMessage || '').trim() || undefined,
                    cc: ccList,
                },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            toast.success(`RFQ sent to ${recipient}`);
            setRfqDialogOpen(false);
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to send RFQ');
        } finally {
            setRfqSending(false);
        }
    };

    // Open the exact stored PDF for a given RFQ send entry.
    const openRfqPdf = async (sendId) => {
        if (!order?.id || !sendId) return;
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(
                `${API_BASE}/api/orders/${order.id}/production-purchase/rfq/sends/${sendId}/pdf`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            if (data?.download_url) window.open(data.download_url, '_blank', 'noopener');
            else toast.error('No stored document for this RFQ');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not open the RFQ document');
        }
    };

    // Open the Mark-Received confirmation dialog. Fetches the
    // server's authoritative preview so the admin sees the exact
    // list of outstanding lines (if any) even when the loaded order
    // page is slightly stale.
    const handleOpenMarkReceivedDialog = async () => {
        if (!order?.id) return;
        setMarkReceivedOpen(true);
        setMarkReceivedLoading(true);
        setMarkReceivedPreview(null);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(
                `${API_BASE}/api/orders/${order.id}/production-purchase/rfq/mark-received/preview`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setMarkReceivedPreview(res.data || null);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed to load RFQ receipt preview');
            setMarkReceivedOpen(false);
        } finally {
            setMarkReceivedLoading(false);
        }
    };

    // Confirm & persist the receipt. The server re-validates
    // outstanding lines + prior-receipt guards, so a race between
    // two admin tabs can only land one success.
    const handleConfirmMarkReceived = async () => {
        if (!order?.id) return;
        setMarkReceivedSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE}/api/orders/${order.id}/production-purchase/rfq/mark-received`,
                {},
                { headers: { Authorization: `Bearer ${token}` } },
            );
            toast.success('Supplier quote marked as received');
            setMarkReceivedOpen(false);
            if (onUpdate) onUpdate();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            // Backend returns a structured 409 body when outstanding
            // lines exist. Surface the message cleanly in both cases.
            const msg = typeof detail === 'object'
                ? (detail?.message || 'Cannot mark received — see outstanding lines.')
                : (detail || 'Failed to mark supplier quote received');
            toast.error(msg);
            // Refresh the preview so the dialog reflects the new state.
            if (typeof detail === 'object' && Array.isArray(detail?.outstanding_lines)) {
                setMarkReceivedPreview(prev => ({
                    ...(prev || {}),
                    can_mark_received: false,
                    outstanding_lines: detail.outstanding_lines,
                    outstanding_count: detail.outstanding_lines.length,
                }));
            }
        } finally {
            setMarkReceivedSubmitting(false);
        }
    };
    
    // Check if invoice is FULLY booked in e-conomic (not just a draft)
    // Allow edits when invoice is still a draft in e-conomic
    const isInvoiceFinallyBooked = invoice?.economic_status === 'booked_in_economic' || invoice?.economic_booked_invoice_number;
    
    // Invoice is editable if it's only a draft in e-conomic (or not sent at all)
    const isInvoiceEditableInEconomic = !isInvoiceFinallyBooked;
    
    // Determine if invoice can be booked based on supplier mode
    const supplierMode = order?.supplier_mode || 'enabled';
    const isNoSupplierMode = supplierMode === 'none';
    
    // For supplier mode: require delivery notice
    // For no supplier mode: allow booking directly (after invoice draft is created)
    const canBookInvoice = isNoSupplierMode 
        ? invoice && !isInvoiceBooked  // No supplier: can book once draft exists
        : order?.supplier_confirmation?.delivery_notice_sent_at;  // With supplier: need delivery notice
    
    // State for creating invoice draft manually
    const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
    
    // Calculate financial summary with proforma support
    // Now uses unified finance summary if available (includes BOM-derived costs)
    const calculateFinancialSummary = () => {
        // Use unified finance summary if available (Phase 1-2)
        if (financeSummary && financeSummary.costs) {
            const productionCost = financeSummary.costs.production?.total || 0;
            const artworkCost = financeSummary.costs.artwork?.total || 0;
            const additionalCost = financeSummary.costs.additional?.total || 0;
            const totalCost = financeSummary.costs.total || (productionCost + artworkCost + additionalCost);
            
            // Revenue in EUR (normalized)
            const actualRevenueEur = financeSummary.actual_revenue_eur || financeSummary.actual_revenue || 0;
            const estimatedRevenueEur = financeSummary.estimated_revenue_eur || financeSummary.estimated_revenue || 0;
            
            // Revenue in original currency (for display in Invoice card)
            const actualRevenueOriginal = financeSummary.actual_revenue_original || null;
            const actualRevenueCurrency = financeSummary.actual_revenue_currency || null;
            const exchangeRateUsed = financeSummary.exchange_rate_used || 1;
            
            return {
                // Costs - from unified summary (already in EUR)
                totalCost,
                productionCost,
                artworkCost,
                manualPurchasesCost: additionalCost,
                hasCost: totalCost > 0,
                hasProductionCost: productionCost > 0,
                productionBreakdown: financeSummary.costs.production?.breakdown || [],
                additionalBreakdown: financeSummary.costs.additional?.breakdown || [],
                
                // Revenue - NORMALIZED TO EUR (for Financial Overview calculations)
                actualRevenue: actualRevenueEur,
                estimatedRevenue: estimatedRevenueEur,
                displayRevenue: actualRevenueEur || estimatedRevenueEur,
                hasActualRevenue: actualRevenueEur > 0,
                hasEstimatedRevenue: estimatedRevenueEur > 0,
                hasRevenue: (actualRevenueEur > 0 || estimatedRevenueEur > 0),
                
                // Revenue - ORIGINAL TRANSACTION VALUES (for Invoice card display)
                actualRevenueOriginal,
                actualRevenueCurrency,
                exchangeRateUsed,
                hasOriginalRevenue: actualRevenueOriginal !== null && actualRevenueCurrency !== null,
                
                // Profit - CALCULATED FROM EUR VALUES ONLY
                actualProfit: financeSummary.actual_profit,
                estimatedProfit: financeSummary.estimated_profit,
                displayProfit: financeSummary.actual_profit ?? financeSummary.estimated_profit,
                marginPercent: financeSummary.actual_margin_pct ?? financeSummary.estimated_margin_pct ?? 0,
                
                // Display flags
                isEstimate: !actualRevenueEur && estimatedRevenueEur > 0,
                revenue: actualRevenueEur || estimatedRevenueEur,
                profit: financeSummary.actual_profit ?? financeSummary.estimated_profit,
                baseCurrency: 'EUR',  // Finance engine base currency
                currency: 'EUR',      // For display in Financial Overview
                
                // Summary flags
                isFullyCosted: financeSummary.is_fully_costed,
                invoiceStatus: financeSummary.invoice_status,
                invoiceBooked: financeSummary.invoice_booked
            };
        }
        
        // Fallback to legacy calculation if unified summary not available
        // Supplier costs (typically EUR)
        const productionCost = purchase?.total_amount || 0;
        const artworkCost = artworkPurchase?.total_amount || 0;
        const manualPurchasesCost = manualPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0);
        const totalCost = productionCost + artworkCost + manualPurchasesCost;
        
        // Revenue from invoice or proforma (net for VAT invoices)
        const actualRevenue = getInvoiceNet(invoice) || 0;
        const hasActualRevenue = invoice && (invoice.unit_price > 0);
        
        const estimatedRevenue = proforma?.total_revenue || 0;
        const hasEstimatedRevenue = proforma?.has_proforma && estimatedRevenue > 0;
        
        const displayRevenue = hasActualRevenue ? actualRevenue : estimatedRevenue;
        const hasRevenue = hasActualRevenue || hasEstimatedRevenue;
        const isEstimate = !hasActualRevenue && hasEstimatedRevenue;
        
        // CURRENCY SAFETY: Only calculate profit when currencies match
        // Invoice may be in DKK while costs are in EUR — mixing them produces incorrect profit
        const revenueCurrency = invoice?.currency || proforma?.currency || 'EUR';
        const costCurrency = purchase?.currency || 'EUR';
        const currenciesMixed = revenueCurrency !== costCurrency && totalCost > 0 && displayRevenue > 0;
        
        const actualProfit = (hasActualRevenue && !currenciesMixed) ? actualRevenue - totalCost : null;
        const estimatedProfit = (hasEstimatedRevenue && !currenciesMixed) ? estimatedRevenue - totalCost : null;
        const displayProfit = actualProfit !== null ? actualProfit : estimatedProfit;
        
        const marginPercent = displayRevenue > 0 && displayProfit !== null ? ((displayProfit / displayRevenue) * 100) : 0;
        
        const hasCost = totalCost > 0;
        
        return {
            totalCost,
            productionCost,
            artworkCost,
            manualPurchasesCost,
            hasCost,
            hasProductionCost: productionCost > 0,
            productionBreakdown: [],
            additionalBreakdown: [],
            actualRevenue,
            estimatedRevenue,
            displayRevenue,
            hasActualRevenue,
            hasEstimatedRevenue,
            hasRevenue,
            // Original values for legacy (same as actual since no conversion)
            actualRevenueOriginal: actualRevenue,
            actualRevenueCurrency: revenueCurrency,
            exchangeRateUsed: 1,
            hasOriginalRevenue: hasActualRevenue,
            actualProfit,
            estimatedProfit,
            displayProfit,
            marginPercent,
            isEstimate,
            revenue: displayRevenue,
            profit: displayProfit,
            baseCurrency: 'EUR',
            currency: currenciesMixed ? revenueCurrency : 'EUR',
            currenciesMixed
        };
    };
    
    const financials = calculateFinancialSummary();
    
    // Handler for manual invoice draft creation (no supplier mode)
    const handleCreateInvoiceDraft = async () => {
        if (!window.confirm('Create invoice draft for this order? This will use order quantities and prices.')) {
            return;
        }
        
        setIsCreatingInvoice(true);
        try {
            await financeApi.createInvoiceDraft(order.id);
            toast.success('Invoice draft created');
            await fetchFinance();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create invoice draft');
        } finally {
            setIsCreatingInvoice(false);
        }
    };

    const [isSyncingFromEconomic, setIsSyncingFromEconomic] = useState(false);
    
    const handleSyncFromEconomic = async () => {
        setIsSyncingFromEconomic(true);
        try {
            const result = await financeApi.syncFromEconomic(order.id);
            const newStatus = result.sync_result?.new_status;
            const msg = newStatus === 'booked_in_economic' 
                ? 'Invoice is booked in e-conomic'
                : newStatus === 'draft_in_economic'
                ? 'Invoice is a draft in e-conomic'
                : 'Sync complete';
            toast.success(msg);
            await fetchFinance();
            if (onUpdate) onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to sync from e-conomic');
        } finally {
            setIsSyncingFromEconomic(false);
        }
    };

    if (isLoading) {
        return <div className="py-4 text-center text-sm text-slate-400">{t('common.loading', 'Loading...')}</div>;
    }

    const content = (
        <div className="space-y-4" data-testid="finance-section">
            {/* Timeline */}
            <div className="flex items-center justify-between p-3 bg-background rounded-lg">
                <TimelineStep
                    icon={Package}
                    title={t('finance.purchase', 'Purchase')}
                    status={timelineStatus.purchase}
                />
                <TimelineStep
                    icon={Truck}
                    title={t('finance.dispatch', 'Dispatch')}
                    status={timelineStatus.dispatch}
                />
                <TimelineStep
                    icon={CheckCircle}
                    title={t('finance.delivery', 'Delivery')}
                    status={timelineStatus.delivery}
                />
                <TimelineStep
                    icon={Receipt}
                    title={t('finance.invoice', 'Invoice')}
                    status={timelineStatus.invoice}
                    isLast
                />
            </div>

            {/* Financial Summary Card */}
            <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-border">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-emerald-600" />
                            Financial Overview
                        </h4>
                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium">
                            Base: EUR
                        </span>
                    </div>
                    {financials.isEstimate && (
                        <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                            Estimate (Pending Dispatch)
                        </span>
                    )}
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                    {/* Purchase Cost */}
                    <div className="space-y-1">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Purchase Cost</p>
                        <p className={`text-lg font-bold ${financials.hasCost ? 'text-red-600' : 'text-slate-300'}`}>
                            {financials.hasCost ? (
                                <>-{financials.totalCost.toLocaleString('da-DK', { minimumFractionDigits: 2 })} <span className="text-xs font-normal">{financials.currency}</span></>
                            ) : (
                                '—'
                            )}
                        </p>
                        {financials.hasCost && (financials.artworkCost > 0 || financials.manualPurchasesCost > 0) && (
                            <div className="text-[10px] text-slate-400 space-y-0.5">
                                {financials.productionCost > 0 && <p>Production: {financials.productionCost.toLocaleString('da-DK')} {financials.currency}</p>}
                                {financials.artworkCost > 0 && <p>Artwork: {financials.artworkCost.toLocaleString('da-DK')} {financials.currency}</p>}
                                {financials.manualPurchasesCost > 0 && <p>Other: {financials.manualPurchasesCost.toLocaleString('da-DK')} {financials.currency}</p>}
                            </div>
                        )}
                    </div>
                    
                    {/* Sales Revenue */}
                    <div className="space-y-1">
                        <div className="flex items-center gap-1">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                                {financials.isEstimate ? 'Est. Revenue' : 'Sales Revenue'}
                            </p>
                            {financials.isEstimate && (
                                <span className="text-[8px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                                    PROFORMA
                                </span>
                            )}
                        </div>
                        <p className={`text-lg font-bold ${financials.hasRevenue ? 'text-emerald-600' : 'text-slate-300'}`}>
                            {financials.hasRevenue ? (
                                <>+{financials.displayRevenue.toLocaleString('da-DK', { minimumFractionDigits: 2 })} <span className="text-xs font-normal">{financials.currency}</span></>
                            ) : (
                                '—'
                            )}
                        </p>
                        {!financials.hasRevenue && (
                            <p className="text-[10px] text-amber-600">Set proforma below</p>
                        )}
                        {financials.hasActualRevenue && financials.hasEstimatedRevenue && financials.actualRevenue !== financials.estimatedRevenue && (
                            <p className="text-[10px] text-slate-400">
                                Est: {financials.estimatedRevenue.toLocaleString('da-DK')} {financials.currency}
                            </p>
                        )}
                    </div>
                    
                    {/* Profit */}
                    <div className="space-y-1">
                        <div className="flex items-center gap-1">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                                {financials.isEstimate ? 'Est. Profit' : 'Profit'}
                            </p>
                            {financials.isEstimate && (
                                <span className="text-[8px] px-1 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                                    EST
                                </span>
                            )}
                        </div>
                        <p className={`text-lg font-bold ${
                            !financials.hasRevenue || !financials.hasCost 
                                ? 'text-slate-300' 
                                : financials.displayProfit >= 0 
                                    ? 'text-emerald-600' 
                                    : 'text-red-600'
                        }`}>
                            {financials.hasRevenue && financials.hasCost && financials.displayProfit !== null ? (
                                <>{financials.displayProfit >= 0 ? '+' : ''}{financials.displayProfit.toLocaleString('da-DK', { minimumFractionDigits: 2 })} <span className="text-xs font-normal">{financials.currency}</span></>
                            ) : (
                                '—'
                            )}
                        </p>
                        {financials.currenciesMixed && (
                            <p className="text-[10px] text-amber-600">Mixed currencies</p>
                        )}
                    </div>
                    
                    {/* Margin */}
                    <div className="space-y-1">
                        <div className="flex items-center gap-1">
                            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
                                {financials.isEstimate ? 'Est. Margin' : 'Margin'}
                            </p>
                        </div>
                        <p className={`text-lg font-bold ${
                            !financials.hasRevenue || !financials.hasCost || financials.displayProfit === null
                                ? 'text-slate-300' 
                                : financials.marginPercent >= 20 
                                    ? 'text-emerald-600' 
                                    : financials.marginPercent >= 0 
                                        ? 'text-amber-600' 
                                        : 'text-red-600'
                        }`}>
                            {financials.hasRevenue && financials.hasCost && financials.displayProfit !== null ? (
                                <>{financials.marginPercent.toFixed(1)}%</>
                            ) : (
                                '—'
                            )}
                        </p>
                        {financials.hasRevenue && financials.hasCost && (
                            <p className="text-[10px] text-slate-400">
                                {financials.marginPercent >= 20 ? 'Healthy margin' : financials.marginPercent >= 0 ? 'Low margin' : 'Negative margin'}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* ==================== SUPPLIER COSTS SECTION ==================== */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                    <Package className="h-4 w-4 text-amber-600" />
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Supplier Costs</h3>
                    <span className="text-xs text-slate-400 font-normal normal-case">
                        ({financials.hasCost ? `${financials.totalCost.toLocaleString('da-DK', { minimumFractionDigits: 2 })} ${financials.currency}` : 'No costs yet'})
                    </span>
                </div>

                {/* Production Purchase - Unified with PO Lines */}
                <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-amber-500" />
                            <span className="text-sm font-semibold text-slate-800">Production Purchase</span>
                            {purchase?.po_number && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                                    PO #{purchase.po_number}
                                </span>
                            )}
                            {purchase?.status && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                    purchase.status === 'Draft' ? 'bg-slate-100 text-slate-600' :
                                    purchase.status === 'Ordered' ? 'bg-blue-100 text-blue-700' :
                                    purchase.status === 'Invoiced' ? 'bg-emerald-100 text-emerald-700' :
                                    'bg-slate-100 text-slate-600'
                                }`}>
                                    {purchase.status}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[10px] text-slate-700"
                                onClick={handleDownloadPurchasePdf}
                                disabled={isDownloadingPurchasePdf || poLines.length === 0}
                                title={poLines.length === 0 ? 'Add BOM lines first' : 'Download supplier-ready PDF'}
                                data-testid="production-purchase-download-pdf-btn"
                            >
                                <Download className="h-3 w-3 mr-1" />
                                {isDownloadingPurchasePdf ? 'Downloading…' : 'PDF'}
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-[10px] text-indigo-700"
                                onClick={handleOpenRfqDialog}
                                disabled={poLines.length === 0 || !!isPurchaseBooked}
                                title={
                                    poLines.length === 0
                                        ? 'Add BOM lines first'
                                        : isPurchaseBooked
                                            ? 'This purchase is already booked — RFQ not available'
                                            : 'Send a Request For Quotation to the supplier'
                                }
                                data-testid="production-purchase-send-rfq-btn"
                            >
                                <Send className="h-3 w-3 mr-1" />
                                Send RFQ
                            </Button>
                            {!isPurchaseBooked && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 text-[10px] text-blue-600"
                                    onClick={() => setIsAddingLine(true)}
                                >
                                    <Plus className="h-3 w-3 mr-1" />
                                    {order?.product_type === 'vinyl' ? 'Add Cost' : 'Add Line'}
                                </Button>
                            )}
                        </div>
                    </div>
                    
                    {/* Supplier & Currency */}
                    {purchase?.supplier && (
                        <div className="px-3 py-1.5 text-xs text-slate-500 border-b border-slate-50 flex items-center gap-3">
                            <span>Supplier: <strong className="text-slate-700">{purchase.supplier}</strong></span>
                            <span>Currency: <strong className="text-slate-700">{purchase.currency || 'EUR'}</strong></span>
                        </div>
                    )}

                    {/* ── Supplier-RFQ status strip ────────────────────
                        Derived purely from the persisted ``order.rfq_sends[]``
                        array (shared helper ``deriveRfqStatus``). Shows up
                        as soon as the first successful send is recorded and
                        remains in place across resends — the latest send
                        timestamp just refreshes. Failed sends never flip the
                        order to "Awaiting Supplier Quote"; they still appear
                        in the history dialog so admins can see them. */}
                    {(() => {
                        const rfq = deriveRfqStatus(order);
                        if (rfq.state === RFQ_STATE.NONE) return null;
                        const latest = rfq.latest;
                        const isReceived = rfq.state === RFQ_STATE.RECEIVED;

                        // "Mark received" is only reachable while the
                        // RFQ is still Awaiting. The green received
                        // strip replaces the amber one once the admin
                        // has explicitly confirmed receipt. Outstanding
                        // (unknown-price) BOM lines gate the button —
                        // the preview endpoint provides the authoritative
                        // list but we can approximate here with a
                        // client-side check that matches the server rule.
                        const outstandingLines = poLines.filter(l =>
                            l.unit_price === null
                            || l.status === 'missing_procurement'
                            || l.status === 'missing_sku_mapping'
                            || l.status === 'missing_unit_cost'
                        );
                        const canMarkReceived = !isReceived && outstandingLines.length === 0;

                        if (isReceived) {
                            return (
                                <div
                                    className="mx-3 mt-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50/60"
                                    data-testid="production-purchase-rfq-status"
                                    data-rfq-state="received"
                                >
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-600 text-white">
                                                    Supplier Quote Received
                                                </span>
                                                {rfq.received_version && (
                                                    <span className="text-[11px] text-emerald-900 font-medium">
                                                        v{rfq.received_version}
                                                    </span>
                                                )}
                                                <span className="text-[11px] text-emerald-700">
                                                    · {rfq.successful_count} send{rfq.successful_count === 1 ? '' : 's'}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-emerald-800 mt-1 break-words">
                                                <span>Marked {formatDateTime(rfq.received_at)}</span>
                                                {rfq.received_by_user_name && (
                                                    <> · by <strong>{rfq.received_by_user_name}</strong></>
                                                )}
                                                {rfq.received_supplier_name && (
                                                    <> · supplier <strong>{rfq.received_supplier_name}</strong></>
                                                )}
                                                {rfq.received_po_reference && (
                                                    <> · PO <strong>#{rfq.received_po_reference}</strong></>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-[10px] text-emerald-700 hover:bg-emerald-100"
                                            onClick={() => setRfqHistoryOpen(true)}
                                            data-testid="production-purchase-view-rfq-history-btn"
                                        >
                                            View RFQ history
                                        </Button>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div
                                className="mx-3 mt-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50/60"
                                data-testid="production-purchase-rfq-status"
                                data-rfq-state="awaiting"
                            >
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-600 text-white">
                                                Awaiting Supplier Quote
                                            </span>
                                            <span className="text-[11px] text-indigo-900 font-medium">
                                                v{latest.document_version}
                                            </span>
                                            <span className="text-[11px] text-indigo-700">
                                                · {rfq.successful_count} send{rfq.successful_count === 1 ? '' : 's'}
                                            </span>
                                            {rfq.failed_count > 0 && (
                                                <span className="text-[11px] text-red-600">
                                                    · {rfq.failed_count} failed
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-[11px] text-indigo-800 mt-1 break-words">
                                            <span>Sent {formatDateTime(latest.sent_at)}</span>
                                            {latest.recipient && (
                                                <> · to <strong>{latest.recipient}</strong></>
                                            )}
                                            {latest.po_reference && (
                                                <> · PO <strong>#{latest.po_reference}</strong></>
                                            )}
                                            {latest.sent_by_user_name && (
                                                <> · by {latest.sent_by_user_name}</>
                                            )}
                                        </div>
                                        {outstandingLines.length > 0 && (
                                            <div
                                                className="mt-1.5 text-[11px] text-amber-800"
                                                data-testid="production-purchase-rfq-outstanding"
                                            >
                                                <strong>{outstandingLines.length} line(s) still need a confirmed price:</strong>
                                                <ul className="mt-0.5 ml-3 list-disc">
                                                    {outstandingLines.slice(0, 5).map(l => (
                                                        <li key={l.id}>
                                                            {l.description}{l.sku ? ` · ${l.sku}` : ''}
                                                        </li>
                                                    ))}
                                                    {outstandingLines.length > 5 && (
                                                        <li className="text-amber-700 italic">
                                                            and {outstandingLines.length - 5} more…
                                                        </li>
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <Button
                                            size="sm"
                                            className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                                            onClick={handleOpenMarkReceivedDialog}
                                            disabled={!canMarkReceived}
                                            title={
                                                outstandingLines.length > 0
                                                    ? `${outstandingLines.length} line(s) still need a price before this RFQ can be marked received`
                                                    : 'Confirm the supplier replied — closes the RFQ and clears Awaiting Supplier Quote'
                                            }
                                            data-testid="production-purchase-mark-rfq-received-btn"
                                        >
                                            Mark Supplier Quote Received
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 text-[10px] text-indigo-700 hover:bg-indigo-100"
                                            onClick={() => setRfqHistoryOpen(true)}
                                            data-testid="production-purchase-view-rfq-history-btn"
                                        >
                                            View RFQ history
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    
                    {/* Missing Items Warning Banner */}
                    {poLines.some(l => l.status === 'missing_procurement' || l.status === 'missing_sku_mapping' || l.status === 'missing_unit_cost') && (
                        <div className="mx-3 mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                <div className="text-xs">
                                    <p className="font-medium text-amber-800">Incomplete BOM Setup</p>
                                    <p className="text-amber-700 mt-0.5">
                                        {poLines.filter(l => l.status === 'missing_procurement').length > 0 && (
                                            <span className="block">
                                                • {poLines.filter(l => l.status === 'missing_procurement').length} item(s) missing from procurement catalog
                                            </span>
                                        )}
                                        {poLines.filter(l => l.status === 'missing_sku_mapping').length > 0 && (
                                            <span className="block">
                                                • {poLines.filter(l => l.status === 'missing_sku_mapping').length} item(s) without SKU mapping
                                            </span>
                                        )}
                                        {poLines.filter(l => l.status === 'missing_unit_cost').length > 0 && (
                                            <span className="block">
                                                • {poLines.filter(l => l.status === 'missing_unit_cost').length} item(s) missing unit cost
                                            </span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Line Items Table */}
                    <div className="px-3 py-2">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b border-slate-100">
                                    <th className="text-left py-1.5 font-medium text-slate-500">Description</th>
                                    <th className="text-right py-1.5 font-medium text-slate-500 w-16">Qty</th>
                                    <th className="text-right py-1.5 font-medium text-slate-500 w-20">Unit</th>
                                    <th className="text-right py-1.5 font-medium text-slate-500 w-20">Total</th>
                                    <th className="w-12"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {poLines.map((line) => {
                                    const isMissing = line.status === 'missing_procurement' || line.status === 'missing_sku_mapping' || line.status === 'missing_unit_cost';
                                    const statusColors = {
                                        'missing_procurement': 'bg-red-50/50 border-l-2 border-red-300',
                                        'missing_sku_mapping': 'bg-amber-50/50 border-l-2 border-amber-300',
                                        'missing_unit_cost': 'bg-amber-50/50 border-l-2 border-amber-300',
                                    };
                                    const rowBg = isMissing 
                                        ? statusColors[line.status] 
                                        : (line.line_type === 'bom' ? '' : 'bg-blue-50/30');
                                    
                                    return (
                                    <tr key={line.id} className={`group ${rowBg}`}>
                                        <td className="py-1.5">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`font-medium ${isMissing ? 'text-slate-500' : 'text-slate-700'}`}>
                                                    {line.description}
                                                </span>
                                                {line.line_type === 'bom' && !isMissing && (
                                                    <span className="text-[9px] px-1 py-0.5 bg-slate-100 text-slate-500 rounded">BOM</span>
                                                )}
                                                {line.line_type === 'adjustment' && (
                                                    <span className="text-[9px] px-1 py-0.5 bg-blue-100 text-blue-600 rounded">Adj</span>
                                                )}
                                                {line.is_override && (
                                                    <span className="text-[9px] px-1 py-0.5 bg-amber-100 text-amber-600 rounded">Override</span>
                                                )}
                                                {/* Status badges for missing items */}
                                                {line.status === 'missing_procurement' && (
                                                    <span className="text-[9px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium">
                                                        Missing Procurement
                                                    </span>
                                                )}
                                                {line.status === 'missing_sku_mapping' && (
                                                    <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded font-medium">
                                                        No SKU Mapping
                                                    </span>
                                                )}
                                                {line.status === 'missing_unit_cost' && (
                                                    <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded font-medium">
                                                        Missing Cost
                                                    </span>
                                                )}
                                                {line.sku && !isMissing && (
                                                    <span className="text-[9px] text-slate-400 font-mono">{line.sku}</span>
                                                )}
                                                {line.sku && isMissing && (
                                                    <span className="text-[9px] text-red-400 font-mono">{line.sku}</span>
                                                )}
                                            </div>
                                            {line.is_override && line.original_unit_price != null && (
                                                <span className="text-[9px] text-slate-400 line-through ml-1">
                                                    was €{line.original_unit_price.toFixed(4)}
                                                </span>
                                            )}
                                            {line.error && (
                                                <div className="text-[9px] text-red-500 mt-0.5">
                                                    {line.error}
                                                </div>
                                            )}
                                            {line.config_key && isMissing && (
                                                <div className="text-[9px] text-slate-400 mt-0.5">
                                                    Config: {line.config_key} = "{line.config_value}"
                                                </div>
                                            )}
                                        </td>
                                        <td className={`py-1.5 text-right ${isMissing ? 'text-slate-400' : 'text-slate-600'}`}>
                                            {isMissing ? '—' : editingLineId === line.id ? (
                                                <Input
                                                    type="number"
                                                    step="1"
                                                    value={editLineForm.quantity}
                                                    onChange={(e) => setEditLineForm(prev => ({ ...prev, quantity: e.target.value }))}
                                                    className="h-6 w-16 text-xs text-right"
                                                />
                                            ) : (
                                                line.quantity?.toLocaleString()
                                            )}
                                        </td>
                                        <td className="py-1.5 text-right">
                                            {isMissing ? (
                                                <span className="text-slate-400">—</span>
                                            ) : editingLineId === line.id ? (
                                                <Input
                                                    type="number"
                                                    step="0.0001"
                                                    value={editLineForm.unit_price}
                                                    onChange={(e) => setEditLineForm(prev => ({ ...prev, unit_price: e.target.value }))}
                                                    className="h-6 w-20 text-xs text-right"
                                                />
                                            ) : isUnitPriceUnknown(line) ? (
                                                /* Genuinely unknown supplier price → show
                                                   "Quote required" instead of a misleading
                                                   €0.0000. Confirmed zero (unit_price === 0)
                                                   still renders through the final branch
                                                   below. See ``isUnitPriceUnknown`` for the
                                                   null-vs-0 distinction. */
                                                <span
                                                    className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium whitespace-nowrap"
                                                    title="Supplier price not yet confirmed — send an RFQ to request it"
                                                    data-testid="po-line-quote-required"
                                                >
                                                    Quote required
                                                </span>
                                            ) : (
                                                <span className="text-slate-600">€{line.unit_price.toFixed(4)}</span>
                                            )}
                                        </td>
                                        <td className={`py-1.5 text-right font-medium ${isMissing ? 'text-slate-400' : 'text-slate-700'}`}>
                                            {isMissing
                                                ? '—'
                                                : isUnitPriceUnknown(line)
                                                    ? <span className="text-amber-600 text-[11px]">—</span>
                                                    : `€${(line.line_total ?? 0).toFixed(2)}`}
                                        </td>
                                        <td className="py-1.5 text-right">
                                            {!isPurchaseBooked && (
                                                <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {editingLineId === line.id ? (
                                                        <>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-5 w-5 p-0 text-emerald-600"
                                                                onClick={() => handleUpdatePoLine(line.id)}
                                                            >
                                                                <Check className="h-3 w-3" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-5 w-5 p-0 text-slate-400"
                                                                onClick={() => setEditingLineId(null)}
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-5 w-5 p-0 text-slate-400 hover:text-blue-600"
                                                                onClick={() => {
                                                                    setEditingLineId(line.id);
                                                                    setEditLineForm({ 
                                                                        quantity: line.quantity,
                                                                        unit_price: line.unit_price 
                                                                    });
                                                                }}
                                                                title="Edit qty/price"
                                                            >
                                                                <Pencil className="h-3 w-3" />
                                                            </Button>
                                                            {line.is_override && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-5 w-5 p-0 text-slate-400 hover:text-amber-600"
                                                                    onClick={() => handleResetOverride(line.id)}
                                                                    title="Reset to original"
                                                                >
                                                                    <RefreshCw className="h-3 w-3" />
                                                                </Button>
                                                            )}
                                                            {line.line_type !== 'bom' && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-5 w-5 p-0 text-slate-400 hover:text-red-600"
                                                                    onClick={() => handleDeletePoLine(line.id)}
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                    );
                                })}
                                
                                {/* Add new line inline form */}
                                {isAddingLine && (
                                    <tr className="bg-emerald-50/50">
                                        <td className="py-1.5">
                                            <Input
                                                placeholder="Description"
                                                value={newLineForm.description}
                                                onChange={(e) => setNewLineForm(prev => ({ ...prev, description: e.target.value }))}
                                                className="h-6 text-xs"
                                                autoFocus
                                            />
                                        </td>
                                        <td className="py-1.5">
                                            <Input
                                                type="number"
                                                value={newLineForm.quantity}
                                                onChange={(e) => setNewLineForm(prev => ({ ...prev, quantity: e.target.value }))}
                                                className="h-6 w-16 text-xs text-right"
                                            />
                                        </td>
                                        <td className="py-1.5">
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={newLineForm.unit_price}
                                                onChange={(e) => setNewLineForm(prev => ({ ...prev, unit_price: e.target.value }))}
                                                className="h-6 w-20 text-xs text-right"
                                            />
                                        </td>
                                        <td className="py-1.5 text-right text-slate-400 text-xs">
                                            €{((parseFloat(newLineForm.quantity) || 0) * (parseFloat(newLineForm.unit_price) || 0)).toFixed(2)}
                                        </td>
                                        <td className="py-1.5 text-right">
                                            <div className="flex items-center justify-end gap-0.5">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-5 w-5 p-0 text-emerald-600"
                                                    onClick={handleAddPoLine}
                                                >
                                                    <Check className="h-3 w-3" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-5 w-5 p-0 text-slate-400"
                                                    onClick={() => {
                                                        setIsAddingLine(false);
                                                        setNewLineForm({ description: '', quantity: 1, unit_price: 0 });
                                                    }}
                                                >
                                                    <X className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot className="border-t border-slate-200">
                                <tr>
                                    <td colSpan="3" className="py-2 text-right font-medium text-slate-600">
                                        {poTotals?.quote_required_count > 0 ? 'Known subtotal (provisional)' : 'Total Production Cost'}
                                    </td>
                                    <td className="py-2 text-right font-bold text-slate-800">
                                        €{(poTotals?.quote_required_count > 0 ? poTotals?.known_subtotal : poTotals?.total)?.toFixed(2) || '0.00'}
                                    </td>
                                    <td></td>
                                </tr>
                                {poTotals?.quote_required_count > 0 && (
                                    <tr className="text-[10px]">
                                        <td colSpan="3" className="pb-1 text-right text-amber-700" data-testid="po-outstanding-quote-count">
                                            {poTotals.quote_required_count} line{poTotals.quote_required_count === 1 ? '' : 's'} awaiting supplier quote
                                        </td>
                                        <td className="pb-1 text-right text-amber-700">
                                            not final
                                        </td>
                                        <td></td>
                                    </tr>
                                )}
                                {poTotals?.quantity > 0 && (
                                    <tr className="text-[10px]">
                                        <td colSpan="3" className="pb-1 text-right text-slate-400">
                                            Unit Cost ({poTotals.quantity?.toLocaleString()} pcs){poTotals?.quote_required_count > 0 ? ' · provisional' : ''}
                                        </td>
                                        <td className="pb-1 text-right text-slate-500">
                                            €{poTotals?.unit_price?.toFixed(4) || '0.0000'}
                                        </td>
                                        <td></td>
                                    </tr>
                                )}
                            </tfoot>
                        </table>
                    </div>
                    
                    {/* BOM Refresh — available for any product type
                        (vinyl / cd / dvd / bluray) as long as the order
                        has at least one BOM-derived Production Purchase
                        line, OR the order carries a production cost
                        snapshot the backend can rebuild lines from
                        (this covers legacy orders that predate the
                        Refresh feature — the button materialises for
                        them once the snapshot is visible). */}
                    {(poLines.some(l => l.line_type === 'bom') ||
                      (order?.production_cost_snapshot?.components?.length > 0)) && (
                        <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
                            <span className="text-[10px] text-slate-400">
                                {poLines.filter(l => l.line_type === 'bom').length > 0 ? (
                                    <>
                                        {poLines.filter(l => l.line_type === 'bom').length} BOM components
                                        {poLines.some(l => l.is_override) && ' • Has overrides'}
                                    </>
                                ) : (
                                    <>No BOM lines yet — rebuild from snapshot</>
                                )}
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 text-[10px] text-blue-600"
                                onClick={refreshBomLines}
                                disabled={isRefreshingBom}
                            >
                                {isRefreshingBom ? (
                                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Refreshing...</>
                                ) : (
                                    <><RefreshCw className="h-3 w-3 mr-1" /> Refresh from BOM</>
                                )}
                            </Button>
                        </div>
                    )}
                    
                    {/* Locked notice */}
                    {isPurchaseBooked && (
                        <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50/50 flex items-center gap-1.5 text-[10px] text-slate-500">
                            <Lock className="h-3 w-3" />
                            <span>Locked - Invoice booked</span>
                        </div>
                    )}
                </div>

                {/* Artwork/Repro Purchase */}
                {artworkJob?.supplier_purchase_po_number && (
                    <PurchaseCard
                        title="Artwork/Repro Purchase"
                        icon={Palette}
                        iconColor="text-purple-500"
                        badgeColor="purple"
                        poNumber={artworkJob.supplier_purchase_po_number}
                        status={artworkPurchase?.status}
                        isLocked={['Invoiced', 'Paid'].includes(artworkPurchase?.status)}
                        supplier={artworkPurchase?.partner_name || artworkJob.partner_name}
                        currency={artworkPurchase?.currency || 'EUR'}
                        totalAmount={artworkPurchase?.total_amount}
                        lineItems={artworkPurchase?.line_items || []}
                        linkedTo="Artwork Job"
                        linkedToLabel="Artwork Job"
                        isEditing={isEditingArtworkPurchase}
                        setIsEditing={setIsEditingArtworkPurchase}
                        isSaving={isSaving}
                        lineItemsForm={artworkPurchaseForm.line_items}
                        setLineItemsForm={(items) => setArtworkPurchaseForm({ line_items: items })}
                        onSave={handleSaveArtworkPurchase}
                        onCancel={() => {
                            setIsEditingArtworkPurchase(false);
                            if (artworkPurchase?.line_items) {
                                setArtworkPurchaseForm({ 
                                    line_items: artworkPurchase.line_items.map(item => ({
                                        ...item,
                                        unit_price: item.unit_price?.toString() || '0'
                                    }))
                                });
                            }
                        }}
                        statusActions={
                            artworkPurchase?.line_items?.length > 0 && !isEditingArtworkPurchase ? [
                                artworkPurchase.status === 'Draft' && {
                                    label: 'Mark as Ordered',
                                    onClick: () => handleUpdateArtworkPurchaseStatus('Ordered'),
                                },
                                artworkPurchase.status === 'Ordered' && {
                                    label: 'Mark as Invoiced',
                                    icon: Lock,
                                    onClick: () => handleUpdateArtworkPurchaseStatus('Invoiced'),
                                },
                                artworkPurchase.status === 'Invoiced' && {
                                    label: 'Mark as Paid',
                                    onClick: () => handleUpdateArtworkPurchaseStatus('Paid'),
                                },
                            ].filter(Boolean) : []
                        }
                    />
                )}

                {/* Manual Supplier Purchases */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <ShoppingCart className="h-4 w-4 text-slate-500" />
                            <span className="text-sm font-medium text-slate-600">Additional Costs</span>
                            {manualPurchases.length > 0 && (
                                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                                    {manualPurchases.length}
                                </span>
                            )}
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => setShowAddPurchaseModal(true)}
                        >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Cost
                        </Button>
                    </div>

                    {/* Manual Purchase Cards */}
                    {manualPurchases.map((mp) => (
                        <PurchaseCard
                            key={mp.id}
                            title={mp.description || 'Additional Cost'}
                            icon={ShoppingCart}
                            iconColor="text-slate-500"
                            badgeColor="amber"
                            poNumber={mp.po_number}
                            status={mp.status}
                            isLocked={['Invoiced', 'Paid'].includes(mp.status)}
                            supplier={mp.partner_name || 'No supplier'}
                            currency={mp.currency || 'EUR'}
                            totalAmount={mp.total_amount}
                            lineItems={mp.line_items || []}
                            linkedTo={null}
                            linkedToLabel={null}
                            statusActions={
                                !['Invoiced', 'Paid'].includes(mp.status) ? [
                                    {
                                        label: 'Edit',
                                        icon: Pencil,
                                        variant: 'outline',
                                        onClick: () => handleEditManualPurchase(mp),
                                    },
                                    mp.status === 'Draft' && {
                                        label: 'Mark as Ordered',
                                        onClick: () => handleUpdateManualPurchaseStatus(mp.id, 'Ordered'),
                                    },
                                    mp.status === 'Ordered' && {
                                        label: 'Mark as Invoiced',
                                        icon: Lock,
                                        onClick: () => handleUpdateManualPurchaseStatus(mp.id, 'Invoiced'),
                                    },
                                    mp.status === 'Invoiced' && {
                                        label: 'Mark as Paid',
                                        onClick: () => handleUpdateManualPurchaseStatus(mp.id, 'Paid'),
                                    },
                                    mp.status === 'Draft' && {
                                        label: 'Delete',
                                        icon: Trash2,
                                        variant: 'ghost',
                                        className: 'text-red-600 hover:text-red-700 hover:bg-red-50',
                                        onClick: () => handleDeleteManualPurchase(mp.id, mp.po_number),
                                    },
                                ].filter(Boolean) : []
                            }
                        />
                    ))}

                    {manualPurchases.length === 0 && (
                        <div className="text-center py-3 text-xs text-slate-400 border border-dashed rounded-lg bg-slate-50/50">
                            No additional costs (freight, extras, etc.)
                        </div>
                    )}
                </div>
            </div>

            {/* ==================== SALES DOCUMENT SECTION ==================== */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                    <Receipt className="h-4 w-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Sales Document</h3>
                    <span className="text-xs text-slate-400 font-normal normal-case">
                        ({financials.hasRevenue 
                            ? `${financials.displayRevenue.toLocaleString('da-DK', { minimumFractionDigits: 2 })} EUR${financials.isEstimate ? ' (Est.)' : ''}` 
                            : 'No pricing yet'})
                    </span>
                </div>

                {/* Unified Sales Document Card */}
                <SalesDocumentCard 
                    invoice={invoice}
                    proforma={proforma}
                    financials={financials}
                    isEditingInvoice={isEditingInvoice}
                    setIsEditingInvoice={setIsEditingInvoice}
                    isEditingProforma={isEditingProforma}
                    setIsEditingProforma={setIsEditingProforma}
                    invoiceForm={invoiceForm}
                    setInvoiceForm={setInvoiceForm}
                    proformaForm={proformaForm}
                    setProformaForm={setProformaForm}
                    handleSaveInvoice={handleSaveInvoice}
                    handleSaveProforma={handleSaveProforma}
                    handleBookInvoice={handleBookInvoice}
                    handleCreateInvoiceDraft={handleCreateInvoiceDraft}
                    handleSyncFromEconomic={handleSyncFromEconomic}
                    isSaving={isSaving}
                    isBooking={isBooking}
                    isCreatingInvoice={isCreatingInvoice}
                    isSyncingFromEconomic={isSyncingFromEconomic}
                    isInvoiceBooked={isInvoiceBooked}
                    canBookInvoice={canBookInvoice}
                    isNoSupplierMode={isNoSupplierMode}
                    formatDateTime={formatDateTime}
                    orderId={order?.id}
                    refetchInvoice={fetchFinance}
                    t={t}
                />
                
                {/* Invoice Basis / Order Breakdown */}
                <InvoiceBasisSection
                    orderId={order?.id}
                    invoiceLines={invoiceLines}
                    setInvoiceLines={setInvoiceLines}
                    onRefresh={fetchInvoiceLines}
                    isLocked={isInvoiceFinallyBooked}
                    isDraftInEconomic={isInvoiceBooked && !isInvoiceFinallyBooked}
                    currency={order?.order_currency || 'EUR'}
                    t={t}
                />
            </div>

            {/* ==================== CUSTOMER QUOTATION SECTION ==================== */}
            {/* Admin-side quote workflow (request → send → approval →
                history). Hoisted here from the top of the admin order
                page so the full quote lifecycle lives next to the Sales
                Document it is generated from. QuoteSection self-hides
                for orders that never entered the quote flow, so this
                sub-section is a no-op for ordinary orders. */}
            <div className="space-y-4">
                <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                    <FileText className="h-4 w-4 text-indigo-600" />
                    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Customer Quotation</h3>
                    {!(order?.status === 'Quote' || order?.status === 'Quoted' || (Array.isArray(order?.quote_versions) && order.quote_versions.length > 0)) && (
                        <span className="text-xs text-slate-400 font-normal normal-case">
                            (not a quote-based order)
                        </span>
                    )}
                </div>
                <QuoteSection order={order} onUpdate={onUpdate} />
            </div>

            {/* Audit Trail Toggle */}
            <button
                onClick={() => setShowEvents(!showEvents)}
                className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            >
                <History className="h-3 w-3" />
                {showEvents ? t('finance.hide_history', 'Hide history') : t('finance.show_history', 'Show history')}
            </button>

            {/* Audit Trail */}
            {showEvents && finance?.timeline && finance.timeline.length > 0 && (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                    {finance.timeline.map((event) => (
                        <div key={event.id} className="text-xs bg-background rounded p-2">
                            <div className="flex items-center justify-between">
                                <span className="font-medium text-slate-700">
                                    {event.event_type.replace(/_/g, ' ')}
                                </span>
                                <span className="text-slate-400">
                                    {formatDateTime(event.created_at)}
                                </span>
                            </div>
                            <p className="text-slate-500">{t('common.by', 'by')} {event.actor_name}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // Modal should be rendered regardless of embedded mode
    const addPurchaseModal = (
        <Dialog open={showAddPurchaseModal} onOpenChange={(open) => {
            if (!open) {
                resetNewPurchaseForm();
                setEditingPurchaseId(null);
            }
            setShowAddPurchaseModal(open);
        }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShoppingCart className="h-5 w-5" />
                        {editingPurchaseId ? 'Edit Additional Cost' : 'Create Manual Supplier Purchase'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Supplier & Currency */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Supplier / Partner</Label>
                            <Select
                                value={newPurchaseForm.partner_id || 'none'}
                                onValueChange={(v) => setNewPurchaseForm(prev => ({ ...prev, partner_id: v === 'none' ? '' : v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select supplier..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No supplier</SelectItem>
                                    {partners.map(p => (
                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Currency</Label>
                            <Select
                                value={newPurchaseForm.currency}
                                onValueChange={(v) => setNewPurchaseForm(prev => ({ ...prev, currency: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="EUR">EUR</SelectItem>
                                    <SelectItem value="DKK">DKK</SelectItem>
                                    <SelectItem value="NOK">NOK</SelectItem>
                                    <SelectItem value="SEK">SEK</SelectItem>
                                    <SelectItem value="USD">USD</SelectItem>
                                    <SelectItem value="GBP">GBP</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label>Description</Label>
                        <Input
                            value={newPurchaseForm.description}
                            onChange={(e) => setNewPurchaseForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Purchase description..."
                        />
                    </div>

                    {/* Add Line Items */}
                    <div className="space-y-3 border-t pt-4">
                        <Label className="text-base font-medium">Line Items</Label>

                        {/* From Price Catalog */}
                        <div className="bg-background p-3 rounded-lg space-y-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                <Search className="h-4 w-4" />
                                Add from Price Catalog
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <Select
                                    value={selectedCatalog || ''}
                                    onValueChange={(v) => {
                                        setSelectedCatalog(v);
                                        fetchCatalogItems(v);
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select catalog..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {catalogs.map(c => (
                                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {catalogItems.length > 0 && (
                                    <Select
                                        value=""
                                        onValueChange={(itemId) => {
                                            const item = catalogItems.find(i => i.id === itemId);
                                            if (item) handleSelectCatalogItem(item);
                                        }}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select item..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {catalogItems.map(item => {
                                                const t = item.tiers?.[0] || {};
                                                const px = t.cost_price ?? t.unit_price ?? t.sales_price ?? t.calculated_sales_price ?? 0;
                                                return (
                                                    <SelectItem key={item.id} value={item.id}>
                                                        {item.name} ({item.sku}) - {Number(px).toFixed(2)} {newPurchaseForm.currency}
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                        </div>

                        {/* Custom Line Item Form */}
                        <div className="bg-background p-3 rounded-lg space-y-3">
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                                <Plus className="h-4 w-4" />
                                {newLineItem.price_item_id ? 'Edit Item from Catalog' : 'Add Custom Item'}
                            </div>
                            <div className="grid grid-cols-6 gap-2">
                                <div className="col-span-2">
                                    <Input
                                        placeholder="Item name *"
                                        value={newLineItem.name}
                                        onChange={(e) => setNewLineItem(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <Input
                                        placeholder="SKU"
                                        value={newLineItem.sku}
                                        onChange={(e) => setNewLineItem(prev => ({ ...prev, sku: e.target.value }))}
                                    />
                                </div>
                                <div>
                                    <Input
                                        type="number"
                                        min="1"
                                        placeholder="Qty"
                                        value={newLineItem.quantity}
                                        onChange={(e) => setNewLineItem(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                                    />
                                </div>
                                <div>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="Price"
                                        value={newLineItem.unit_price}
                                        onChange={(e) => setNewLineItem(prev => ({ ...prev, unit_price: parseFloat(e.target.value) || 0 }))}
                                    />
                                </div>
                                <div>
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="w-full h-9"
                                        onClick={handleAddLineItem}
                                        disabled={!newLineItem.name}
                                    >
                                        <Plus className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Line Items Table */}
                        {newPurchaseForm.line_items.length > 0 && (
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-background">
                                        <tr>
                                            <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Item</th>
                                            <th className="text-right px-3 py-2 text-xs font-medium text-slate-500 uppercase w-24">Qty</th>
                                            <th className="text-right px-3 py-2 text-xs font-medium text-slate-500 uppercase w-28">Unit Price</th>
                                            <th className="text-right px-3 py-2 text-xs font-medium text-slate-500 uppercase w-24">Total</th>
                                            <th className="w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {newPurchaseForm.line_items.map((item, idx) => (
                                            <tr key={item._tempId || idx}>
                                                <td className="px-3 py-2">
                                                    <Input
                                                        value={item.name}
                                                        onChange={(e) => {
                                                            const v = e.target.value;
                                                            setNewPurchaseForm(prev => ({
                                                                ...prev,
                                                                line_items: prev.line_items.map((li, i) => i === idx ? { ...li, name: v } : li)
                                                            }));
                                                        }}
                                                        className="h-8 text-sm"
                                                        data-testid={`line-item-name-${idx}`}
                                                    />
                                                    <div className="flex items-center gap-2 mt-1">
                                                        {item.sku && <span className="text-xs text-slate-400">({item.sku})</span>}
                                                        {item.price_item_id && (
                                                            <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Catalog</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        value={item.quantity}
                                                        onChange={(e) => {
                                                            const v = parseFloat(e.target.value) || 0;
                                                            setNewPurchaseForm(prev => ({
                                                                ...prev,
                                                                line_items: prev.line_items.map((li, i) => i === idx ? { ...li, quantity: v } : li)
                                                            }));
                                                        }}
                                                        className="h-8 text-sm text-right"
                                                        data-testid={`line-item-qty-${idx}`}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-right">
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={item.unit_price}
                                                        onChange={(e) => {
                                                            const v = parseFloat(e.target.value) || 0;
                                                            setNewPurchaseForm(prev => ({
                                                                ...prev,
                                                                line_items: prev.line_items.map((li, i) => i === idx ? { ...li, unit_price: v } : li)
                                                            }));
                                                        }}
                                                        className="h-8 text-sm text-right"
                                                        data-testid={`line-item-price-${idx}`}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 text-right font-medium text-slate-700">
                                                    {(Number(item.unit_price || 0) * Number(item.quantity || 0)).toFixed(2)}
                                                </td>
                                                <td className="px-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => handleRemoveLineItem(idx)}
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-background border-t">
                                        <tr>
                                            <td colSpan="3" className="px-3 py-2 text-right font-medium text-slate-600">Total</td>
                                            <td className="px-3 py-2 text-right font-bold text-slate-900">
                                                {getNewPurchaseTotal().toFixed(2)} {newPurchaseForm.currency}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                        <Label>Notes (optional)</Label>
                        <Textarea
                            value={newPurchaseForm.notes}
                            onChange={(e) => setNewPurchaseForm(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Internal notes..."
                            rows={2}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => { setShowAddPurchaseModal(false); setEditingPurchaseId(null); }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateManualPurchase}
                        disabled={isSaving || newPurchaseForm.line_items.length === 0}
                    >
                        {isSaving 
                            ? (editingPurchaseId ? 'Saving...' : 'Creating...')
                            : (editingPurchaseId ? 'Save Changes' : 'Create Purchase')
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    // ──────────── RFQ (Request For Quotation) dialog ────────────
    // Admin-only. Does NOT create a PO, invoice or state transition.
    // Pre-filled from GET /orders/{id}/production-purchase/rfq/defaults
    // when opened — see handleOpenRfqDialog above.
    const rfqSendModal = (
        <Dialog open={rfqDialogOpen} onOpenChange={(open) => {
            if (!open) {
                setRfqDefaults(null);
            }
            setRfqDialogOpen(open);
        }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rfq-send-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Send className="h-5 w-5 text-indigo-600" />
                        Send Request For Quotation
                    </DialogTitle>
                </DialogHeader>
                {rfqLoading && (
                    <div className="py-6 flex items-center justify-center text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading supplier defaults…
                    </div>
                )}
                {!rfqLoading && rfqDefaults && (
                    <div className="space-y-4 py-2">
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                            This emails a <strong>Request For Quotation</strong> PDF to the supplier.
                            It will <strong>not</strong> create a purchase order, generate an invoice, or
                            change the order status. Lines without a confirmed unit price are marked
                            <em> "Quote required"</em> on the PDF.
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-xs text-slate-600">
                            <div>
                                <span className="text-slate-400">Order:</span>{' '}
                                <strong className="text-slate-800">#{rfqDefaults.order_number || order?.id}</strong>
                            </div>
                            <div>
                                <span className="text-slate-400">Purchase Order:</span>{' '}
                                <strong className="text-slate-800">
                                    {rfqDefaults.purchase_order_number ? `#${rfqDefaults.purchase_order_number}` : '—'}
                                </strong>
                            </div>
                            <div>
                                <span className="text-slate-400">BOM lines:</span>{' '}
                                <strong className="text-slate-800">{rfqDefaults.line_count}</strong>
                                {rfqDefaults.quote_required_count > 0 && (
                                    <span className="ml-1 text-amber-700">
                                        ({rfqDefaults.quote_required_count} awaiting quote)
                                    </span>
                                )}
                            </div>
                            <div>
                                <span className="text-slate-400">Supplier:</span>{' '}
                                <strong className="text-slate-800">
                                    {rfqDefaults.partner_name || '— not resolved —'}
                                </strong>
                            </div>
                            <div>
                                <span className="text-slate-400">Previously sent:</span>{' '}
                                <strong className="text-slate-800">
                                    {rfqDefaults.send_count || 0} time{(rfqDefaults.send_count || 0) === 1 ? '' : 's'}
                                </strong>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="rfq-recipient" className="text-xs">Recipient email</Label>
                            <Input
                                id="rfq-recipient"
                                value={rfqRecipient}
                                onChange={(e) => setRfqRecipient(e.target.value)}
                                placeholder="supplier@example.com"
                                data-testid="rfq-recipient-input"
                            />
                            <p className="text-[11px] text-slate-500 mt-1">
                                {rfqDefaults.recipient_resolution === 'rfq_email' && (
                                    <>Using the supplier's dedicated <strong>RFQ email</strong>. Edits here apply only to this send.</>
                                )}
                                {rfqDefaults.recipient_resolution === 'email_to' && (
                                    <>No dedicated RFQ email on the supplier record — using the general contact. Edits here apply only to this send.</>
                                )}
                                {rfqDefaults.recipient_resolution === 'not_resolved' && (
                                    <>Supplier could not be auto-resolved. Please enter a recipient manually.</>
                                )}
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="rfq-cc" className="text-xs">CC (optional)</Label>
                            <Input
                                id="rfq-cc"
                                value={rfqCc}
                                onChange={(e) => setRfqCc(e.target.value)}
                                placeholder="cc1@example.com, cc2@example.com"
                                data-testid="rfq-cc-input"
                            />
                            <p className="text-[11px] text-slate-500 mt-1">
                                Comma-separated. Pre-filled from the supplier's RFQ CC defaults — add or remove for this send only.
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="rfq-subject" className="text-xs">Subject</Label>
                            <Input
                                id="rfq-subject"
                                value={rfqSubject}
                                onChange={(e) => setRfqSubject(e.target.value)}
                                data-testid="rfq-subject-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="rfq-message" className="text-xs">Message</Label>
                            <Textarea
                                id="rfq-message"
                                value={rfqMessage}
                                onChange={(e) => setRfqMessage(e.target.value)}
                                rows={7}
                                data-testid="rfq-message-input"
                            />
                        </div>

                        {Array.isArray(rfqDefaults.last_send) === false && rfqDefaults.last_send && (
                            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                                Last send: <strong>{rfqDefaults.last_send.recipient}</strong>{' '}
                                on {formatDateTime(rfqDefaults.last_send.sent_at)}{' '}
                                — v{rfqDefaults.last_send.document_version}{' '}
                                ({rfqDefaults.last_send.status})
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter className="gap-2">
                    <Button
                        variant="outline"
                        onClick={handlePreviewRfqPdf}
                        disabled={rfqLoading || rfqSending}
                        data-testid="rfq-preview-btn"
                    >
                        <Download className="h-3.5 w-3.5 mr-1" />
                        Preview PDF
                    </Button>
                    <div className="flex-1" />
                    <Button
                        variant="outline"
                        onClick={() => setRfqDialogOpen(false)}
                        disabled={rfqSending}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSendRfq}
                        disabled={rfqLoading || rfqSending || !rfqRecipient}
                        data-testid="rfq-confirm-send-btn"
                    >
                        {rfqSending ? (
                            <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Sending…</>
                        ) : (
                            <><Send className="h-3.5 w-3.5 mr-1" /> Send RFQ</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    // ──────────── RFQ History dialog ────────────
    // Read-only inspection of previous sends. Pulls directly from
    // ``order.rfq_sends[]`` — the server already surfaces the same
    // array via ``GET /orders/{id}/production-purchase/rfq/history``,
    // but we already have the data loaded on the order so we render
    // it offline. The server endpoint is still available for direct
    // link-outs / future use.
    const rfqHistoryModal = (() => {
        const rfq = deriveRfqStatus(order);
        // Order newest first for display.
        const historyRows = [...(rfq.all_sends || [])].sort((a, b) =>
            new Date(b?.sent_at || 0) - new Date(a?.sent_at || 0),
        );
        return (
            <Dialog open={rfqHistoryOpen} onOpenChange={setRfqHistoryOpen}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rfq-history-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Send className="h-5 w-5 text-indigo-600" />
                            Supplier RFQ history
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-2 text-xs text-slate-500">
                        Source of truth: <code className="font-mono">order.rfq_sends[]</code>.
                        Entries are preserved on every resend — nothing is overwritten.
                        A send is marked <strong className="text-emerald-700">sent</strong> only
                        after the email provider confirms acceptance; failed attempts are kept
                        as <strong className="text-red-700">failed</strong> for audit.
                    </div>
                    {historyRows.length === 0 ? (
                        <div className="py-6 text-center text-sm text-slate-500">
                            No RFQs have been sent for this order yet.
                        </div>
                    ) : (
                        <div className="rounded-lg border border-slate-200 overflow-hidden">
                            <table className="w-full text-xs" data-testid="rfq-history-table">
                                <thead className="bg-slate-50 text-slate-500">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-medium">When</th>
                                        <th className="text-left px-3 py-2 font-medium">Supplier</th>
                                        <th className="text-left px-3 py-2 font-medium">Recipient</th>
                                        <th className="text-left px-3 py-2 font-medium">PO ref</th>
                                        <th className="text-right px-3 py-2 font-medium">Version</th>
                                        <th className="text-left px-3 py-2 font-medium">Sent by</th>
                                        <th className="text-left px-3 py-2 font-medium">Status</th>
                                        <th className="text-left px-3 py-2 font-medium">Document</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {historyRows.map((row, idx) => (
                                        <tr key={row.id || `${row.rfq_reference}-${idx}`} data-testid={`rfq-history-row-${idx}`}>
                                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                                {formatDateTime(row.sent_at)}
                                            </td>
                                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                                {row.partner_name || '—'}
                                            </td>
                                            <td className="px-3 py-2 text-slate-700 break-all">
                                                {row.recipient || '—'}
                                                {Array.isArray(row.cc) && row.cc.length > 0 && (
                                                    <div className="text-[10px] text-slate-400">
                                                        cc: {row.cc.join(', ')}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                                {row.po_reference ? `#${row.po_reference}` : '—'}
                                            </td>
                                            <td className="px-3 py-2 text-slate-700 text-right whitespace-nowrap">
                                                v{row.document_version || '?'}
                                            </td>
                                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                                                {row.sent_by_user_name || row.sent_by_user_id || '—'}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {row.status === 'sent' ? (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
                                                        Sent
                                                    </span>
                                                ) : row.status === 'failed' ? (
                                                    <span
                                                        className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium"
                                                        title={row.error || ''}
                                                    >
                                                        Failed
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                                                        {row.status || 'Unknown'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {row.pdf_stored || row.pdf_key ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => openRfqPdf(row.id)}
                                                        className="inline-flex items-center gap-1 text-indigo-700 hover:underline"
                                                        data-testid={`rfq-history-open-pdf-${idx}`}
                                                    >
                                                        <Download className="h-3 w-3" /> Open
                                                    </button>
                                                ) : (
                                                    <span className="text-[10px] text-slate-400" title="This RFQ was sent before document archiving was enabled; the original PDF is only in the supplier's inbox.">
                                                        Not stored
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRfqHistoryOpen(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    })();

    // ──────────── Mark Supplier Quote Received dialog ────────────
    // Explicit admin confirmation that the supplier reply has landed
    // and all RFQ-required BOM lines now carry a confirmed price.
    // Backend ``mark-received/preview`` endpoint supplies the exact
    // outstanding-line list so the dialog never guesses — if the
    // server says blocked, the button is blocked.
    const markReceivedModal = (
        <Dialog open={markReceivedOpen} onOpenChange={(open) => {
            if (!open) setMarkReceivedPreview(null);
            setMarkReceivedOpen(open);
        }}>
            <DialogContent className="max-w-xl" data-testid="rfq-mark-received-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Check className="h-5 w-5 text-emerald-600" />
                        Mark Supplier Quote Received
                    </DialogTitle>
                </DialogHeader>
                {markReceivedLoading && (
                    <div className="py-6 flex items-center justify-center text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Checking BOM lines…
                    </div>
                )}
                {!markReceivedLoading && markReceivedPreview && (
                    <div className="space-y-3 py-2">
                        {markReceivedPreview.already_received ? (
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                                This RFQ was already marked received on{' '}
                                <strong>{formatDateTime(markReceivedPreview.received_at)}</strong>
                                {markReceivedPreview.received_by_user_name && (
                                    <> by <strong>{markReceivedPreview.received_by_user_name}</strong></>
                                )}
                                {markReceivedPreview.received_rfq_reference && (
                                    <> ({markReceivedPreview.received_rfq_reference})</>
                                )}.
                            </div>
                        ) : markReceivedPreview.outstanding_count > 0 ? (
                            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                <div className="font-medium">
                                    {markReceivedPreview.outstanding_count} line(s) still need a confirmed supplier price:
                                </div>
                                <ul
                                    className="mt-1 ml-4 list-disc"
                                    data-testid="rfq-mark-received-outstanding"
                                >
                                    {markReceivedPreview.outstanding_lines.map(l => (
                                        <li key={l.id}>
                                            {l.description}{l.sku ? ` · ${l.sku}` : ''}
                                            {l.reason && (
                                                <span className="text-amber-700"> — {l.reason.replaceAll('_', ' ')}</span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-2">
                                    Edit each line's unit price in the Production Purchase table above,
                                    then re-open this dialog.
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                                All RFQ-required BOM lines now carry a confirmed price. Confirming will:
                                <ul className="mt-1 ml-4 list-disc">
                                    <li>Change the RFQ status to <strong>Supplier Quote Received</strong>.</li>
                                    <li>Remove this order from Needs Attention → Awaiting Supplier Quote.</li>
                                    <li>Record a timestamped audit event in System Events.</li>
                                </ul>
                                <div className="mt-2 text-emerald-900">
                                    This does <strong>not</strong> create or send a Production PO —
                                    use the existing Supplier → Production Order flow for that.
                                </div>
                                {markReceivedPreview.latest_send && (
                                    <div className="mt-2 text-emerald-900">
                                        RFQ reference:{' '}
                                        <strong>{markReceivedPreview.latest_send.rfq_reference}</strong>{' '}
                                        (v{markReceivedPreview.latest_send.document_version})
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => setMarkReceivedOpen(false)}
                        disabled={markReceivedSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmMarkReceived}
                        disabled={
                            markReceivedSubmitting
                            || markReceivedLoading
                            || !markReceivedPreview
                            || !markReceivedPreview.can_mark_received
                        }
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="rfq-mark-received-confirm-btn"
                    >
                        {markReceivedSubmitting ? (
                            <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Confirming…</>
                        ) : (
                            <><Check className="h-3.5 w-3.5 mr-1" /> Confirm quote received</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );

    if (embedded) {
        return (
            <>
                {content}
                {addPurchaseModal}
                {rfqSendModal}
                {rfqHistoryModal}
                {markReceivedModal}
            </>
        );
    }

    return (
        <>
            <div className="bg-white rounded-lg border border-slate-300 shadow-premium-sm p-4">
                <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                    <h3 className="text-sm font-medium text-slate-700">{t('finance.title', 'Finance')}</h3>
                </div>
                {content}
            </div>
            {addPurchaseModal}
            {rfqSendModal}
            {rfqHistoryModal}
            {markReceivedModal}
        </>
    );
}
